import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  updateProduct,
  bulkUpdateProducts,
  patchProductStatus,
  deleteProduct,
  fetchDosageForms,
  fetchStrengthUnits,
  fetchHqCountries,
  fetchSupplierNames,
} from '../services/supplierService';
import { fetchAllSupplierProducts, fetchProductDetail, getDashboardToken } from '../services/dashboardService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearch } from '../context/useSearch';
import useUser from '../auth/useUser';
import { useMsal } from '@azure/msal-react';
import AutocompleteInput from '../components/AutocompleteInput';
import MultiSelect from '../components/MultiSelect';
import SearchableSelect from '../components/SearchableSelect';
import CsvUploadModal from '../components/modals/CsvUploadModal';
import HistoricalDataModal from '../components/modals/HistoricalDataModal';
import UploadProductsModal, { UploadSummary } from '../components/modals/UploadProductsModal';
import { useUploadTracking } from '../context/UploadTrackingContext';
import './Suppliers.css';

/* ---------- Helpers ---------- */

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};
const truncate = (s, n) => {
  if (!s) return '';
  const str = String(s);
  return str.length > n ? str.slice(0, n) + '…' : str;
};
const arrOrStr = (v) => {
  if (!v) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
};
const maBadgeClass = (v) => {
  if (!v) return null;
  const val = String(v).toLowerCase();
  if (val.includes('market'))      return 'green';
  if (val.includes('in progress')) return 'amber';
  if (val.includes('pending'))     return 'blue';
  if (val.includes('expected'))    return 'orange-text';
  return null;
};
const isNL = (countries) => {
  if (!countries) return false;
  const v = String(countries).toLowerCase();
  return v.includes('nl') || v.includes('netherlands');
};
const uniqueValues = (rows, key) => {
  const set = new Set();
  for (const r of rows) {
    const v = r[key];
    if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

const fmtUploadedBy = (v) => {
  if (!v) return '—';
  const s = String(v);
  if (s.toLowerCase().startsWith('upload_enriched')) return 'System Import';
  return s;
};
const displayUploadedBy = (createdBy) => {
  if (!createdBy) return '—';
  const match = String(createdBy).match(/^PDFParse \(uploaded by (.+)\)$/);
  return match ? match[1] : createdBy;
};
const euAvailability = (countries) => {
  if (!countries || String(countries).trim() === '') return 'N/A';
  return String(countries).toLowerCase().includes('eu') ? 'YES' : 'No';
};

const STATUS_OPTIONS = ['Pending', 'Approved', 'Rejected'];
const normalizeStatus = (v) => {
  if (!v || !String(v).trim()) return 'Pending';
  const lc = String(v).trim().toLowerCase();
  if (lc === 'pending') return 'Pending';
  if (lc === 'approved') return 'Approved';
  if (lc === 'rejected') return 'Rejected';
  return 'Pending';
};
const statusColor = (v) => {
  if (v === 'Approved') return 'var(--green)';
  if (v === 'Rejected') return 'var(--red)';
  return 'var(--t3)';
};

const PATENT_OPTIONS = ['Patented', 'Off Patent', 'Patent Expiring', 'Unknown'];

const DOSSIER_DATE_OPTIONS = ['Available',
  '2021-Q1','2021-Q2','2021-Q3','2021-Q4','2022-Q1','2022-Q2','2022-Q3','2022-Q4',
  '2023-Q1','2023-Q2','2023-Q3','2023-Q4','2024-Q1','2024-Q2','2024-Q3','2024-Q4',
  '2025-Q1','2025-Q2','2025-Q3','2025-Q4','2026-Q1','2026-Q2','2026-Q3','2026-Q4',
  '2027-Q1','2027-Q2','2027-Q3','2027-Q4','2028-Q1','2028-Q2','2028-Q3','2028-Q4',
  '2029-Q1','2029-Q2','2029-Q3','2029-Q4','2030-Q1','2030-Q2','2030-Q3','2030-Q4'];

/* ---------- Edit modals (mirrored from Suppliers.jsx) ---------- */

function Field({ label, wide, children }) {
  return (
    <div className={wide ? 'col-12' : 'col-12 col-md-6'}>
      <div className="sup-field-label">{label}</div>
      {children}
    </div>
  );
}

function isValidDate(s) {
  if (!s) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s)) && !Number.isNaN(new Date(s).getTime());
}

function SectionHeading({ title }) {
  return (
    <div className="col-12" style={{ borderTop: '1px solid #dee2e6', paddingTop: 12, marginTop: 4 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
    </div>
  );
}

const SECTION_TINTS = {
  general: '#eef4fb',
  market: '#f0f7f0',
  atc: '#fdf6ec',
  manufacturing: '#f5f0f8',
  other: '#f3f4f6',
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function SectionBlock({ bg, children }) {
  return (
    <div className="col-12" style={{ background: bg, borderRadius: 7, padding: '14px 14px 6px' }}>
      <div className="row g-3">{children}</div>
    </div>
  );
}

const XREF_TINT = '#fef9f3';
const SOURCE_LABELS = { internal: 'FocusOne', ema: 'EMA Register', dutch: 'Dutch CBG-MEB', ansm: 'ANSM France' };

function splitInns(inn, combines) {
  if (!inn || !String(inn).trim()) return [];
  const s = String(inn).trim();
  if (combines === 'Yes' || s.includes('+') || s.includes(',')) {
    return s.split(/\s*[+,/]\s*/).map(v => v.trim()).filter(Boolean);
  }
  return [s];
}

function CrossReferenceSection({ inn, combines }) {
  const inns = useMemo(() => splitInns(inn, combines), [inn, combines]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (inns.length === 0) return;
    setLoading(true);
    const params = new URLSearchParams();
    inns.forEach(i => params.append('inn', i));
    fetch(`${import.meta.env.VITE_API_URL}/api/cross-reference/summary?${params}`)
      .then(r => r.json())
      .then(d => setData(d.results || []))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [inns]);

  if (inns.length === 0) {
    return (
      <SectionBlock bg={XREF_TINT}>
        <SectionHeading title="Cross-Reference" />
        <div className="col-12" style={{ fontSize: 13, color: 'var(--t3)', paddingBottom: 8 }}>No INN available for cross-reference</div>
      </SectionBlock>
    );
  }

  return (
    <>
      <SectionBlock bg={XREF_TINT}>
        <SectionHeading title="Cross-Reference" />
        <div className="col-12" style={{ paddingBottom: 8 }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--t3)' }}>Loading cross-reference data...</div>}
          {!loading && data && data.map((entry, idx) => (
            <div key={idx} style={{ marginBottom: idx < data.length - 1 ? 10 : 0 }}>
              {['internal', 'ema', 'dutch', 'ansm'].map(src => (
                <div key={src} style={{ fontSize: 12.5, color: 'var(--t1)', lineHeight: 1.7 }}>
                  <strong>{entry.inn}</strong> found in {entry[src].product_count} products – From {entry[src].supplier_count} different suppliers in {SOURCE_LABELS[src]}
                </div>
              ))}
            </div>
          ))}
          {!loading && data && (
            <button
              type="button"
              onClick={() => setShowDetail(true)}
              style={{ marginTop: 6, background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <i className="bi bi-caret-right-fill" style={{ fontSize: 10 }}></i> Click here to see details
            </button>
          )}
        </div>
      </SectionBlock>
      {showDetail && <CrossReferenceDetailModal inns={inns} onClose={() => setShowDetail(false)} />}
    </>
  );
}

function CrossReferenceDetailModal({ inns, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    inns.forEach(i => params.append('inn', i));
    fetch(`${import.meta.env.VITE_API_URL}/api/cross-reference/details?${params}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData({ internal: [], ema: [], dutch: [], ansm: [] }))
      .finally(() => setLoading(false));
  }, [inns]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const title = inns.join(' + ');

  const renderTable = (label, rows) => (
    <div style={{ flex: '1 1 48%', minWidth: 320, background: 'var(--card)', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>{label}</div>
      {rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No matches found in {label}</div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>INN</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Dosage Form</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Supplier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '5px 10px' }}>{r.inn || '—'}</td>
                  <td style={{ padding: '5px 10px' }}>{r.dosage_form || '—'}</td>
                  <td style={{ padding: '5px 10px' }}>{r.supplier || '—'}</td>
                </tr>
              ))}
              {rows.length >= 500 && (
                <tr><td colSpan={3} style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--t3)', fontSize: 11, fontStyle: 'italic' }}>Showing 500 results — refine your search</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1070, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'xrefFadeIn 200ms ease-out' }}
    >
      <style>{`
        @keyframes xrefFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes xrefScaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '90%', maxWidth: 1400, maxHeight: '85vh', background: '#fff', borderRadius: 14, display: 'flex', flexDirection: 'column', animation: 'xrefScaleIn 250ms ease-out' }}
      >
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">Cross-Reference Details for {title}</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--t3)' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status" />
              Loading cross-reference data...
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {renderTable('Internal Data (FocusOne)', data?.internal || [])}
              {renderTable('EMA Register', data?.ema || [])}
              {renderTable('Dutch CBG-MEB Data', data?.dutch || [])}
              {renderTable('ANSM France', data?.ansm || [])}
            </div>
          )}
        </div>
        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px' }}>
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SingleEditModal({ row: rowProp, onClose, onSaved, onToast, dosageForms, strengthUnits, editorName, acquireToken }) {
  const [fullRow, setFullRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);

  // Fetch full row detail on open (covers any columns trimmed from the list endpoint)
  useEffect(() => {
    const pid = rowProp.product_id || rowProp.id;
    if (!pid) { setFullRow(rowProp); setDetailLoading(false); return; }
    (async () => {
      try {
        let token = acquireToken ? await acquireToken() : undefined;
        let detail;
        try {
          detail = await fetchProductDetail(pid, token);
        } catch (e) {
          if (e?.response?.status === 401 && acquireToken) {
            token = await acquireToken(true);
            detail = await fetchProductDetail(pid, token);
          } else throw e;
        }
        setFullRow({ ...rowProp, ...detail });
      } catch {
        setFullRow(rowProp);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [rowProp, acquireToken]);

  const row = fullRow || rowProp;
  const rawDossierDate = row.dossier_preparation_planned_date || '';
  const dossierDateMatches = DOSSIER_DATE_OPTIONS.includes(rawDossierDate);
  const isInactive = row.product_inactive === 'Yes';
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState({
    supplier_product_name: row.supplier_product_name || '',
    inn: row.inn || '',
    combines_molecules: row.combines_molecules || 'No',
    dosage_form: row.dosage_form || '',
    strength_value_1: row.strength_value_1 ?? '',
    strength_unit_code_1: row.strength_unit_code_1 || '',
    pack_size: row.pack_size || '',
    reference_product_name: row.reference_product_name || '',
    market_auth_countries: row.market_auth_countries || '',
    nl_market_available: row.nl_market_available || '',
    nl_general_ma_status: row.nl_general_ma_status || '',
    supplier_ma_status: row.supplier_ma_status || '',
    ma_registration_number: row.ma_registration_number || '',
    ma_approved_by: row.ma_approved_by || '',
    ma_additional_info: row.ma_additional_info || '',
    dossier_availability_status: row.dossier_availability_status || '',
    dossier_preparation_planned_date: dossierDateMatches ? rawDossierDate : '',
    atc_code: row.atc_code || '',
    atc_classification_name: row.atc_classification_name || '',
    atc_category: row.atc_category || '',
    manufacturing_sites: row.manufacturing_sites || '',
    patent_status: row.patent_status || 'Unknown',
    patent_expiry_date: row.patent_expiry_date || '',
    patent_notes: row.patent_notes || '',
    therapeutical_indication: arrOrStr(row.therapeutical_indication),
    additional_comments: arrOrStr(row.additional_comments),
  });
  const [saving, setSaving] = useState(false);
  const [dossierDateTouched, setDossierDateTouched] = useState(false);
  const [reactivationConfirmed, setReactivationConfirmed] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);

  // Re-initialize form fields when full detail arrives
  useEffect(() => {
    if (!fullRow) return;
    const dd = fullRow.dossier_preparation_planned_date || '';
    setForm({
      supplier_product_name: fullRow.supplier_product_name || '',
      inn: fullRow.inn || '',
      combines_molecules: fullRow.combines_molecules || 'No',
      dosage_form: fullRow.dosage_form || '',
      strength_value_1: fullRow.strength_value_1 ?? '',
      strength_unit_code_1: fullRow.strength_unit_code_1 || '',
      pack_size: fullRow.pack_size || '',
      reference_product_name: fullRow.reference_product_name || '',
      market_auth_countries: fullRow.market_auth_countries || '',
      nl_market_available: fullRow.nl_market_available || '',
      nl_general_ma_status: fullRow.nl_general_ma_status || '',
      supplier_ma_status: fullRow.supplier_ma_status || '',
      ma_registration_number: fullRow.ma_registration_number || '',
      ma_approved_by: fullRow.ma_approved_by || '',
      ma_additional_info: fullRow.ma_additional_info || '',
      dossier_availability_status: fullRow.dossier_availability_status || '',
      dossier_preparation_planned_date: DOSSIER_DATE_OPTIONS.includes(dd) ? dd : '',
      atc_code: fullRow.atc_code || '',
      atc_classification_name: fullRow.atc_classification_name || '',
      atc_category: fullRow.atc_category || '',
      manufacturing_sites: fullRow.manufacturing_sites || '',
      patent_status: fullRow.patent_status || 'Unknown',
      patent_expiry_date: fullRow.patent_expiry_date || '',
      patent_notes: fullRow.patent_notes || '',
      therapeutical_indication: arrOrStr(fullRow.therapeutical_indication),
      additional_comments: arrOrStr(fullRow.additional_comments),
    });
  }, [fullRow]);

  const dis = isInactive && !reactivationConfirmed;
  const disStyle = { color: 'var(--t3)', background: '#f8f9fa' };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => set(k, e.target.value);

  const save = async () => {
    setSaving(true);
    try {
      if (isInactive && reactivationConfirmed) {
        const updated = await updateProduct(row.id || row.product_id, { product_inactive: 'No' }, editorName);
        onSaved(updated);
        onToast({ type: 'ok', msg: 'Product reactivated successfully' });
      } else {
        const payload = { ...form };
        if (payload.therapeutical_indication)
          payload.therapeutical_indication = String(payload.therapeutical_indication)
            .split('\n').map(s => s.trim()).filter(Boolean);
        if (payload.additional_comments)
          payload.additional_comments = String(payload.additional_comments)
            .split('\n').map(s => s.trim()).filter(Boolean);
        if (!isValidDate(payload.patent_expiry_date)) delete payload.patent_expiry_date;
        if (!dossierDateTouched || !payload.dossier_preparation_planned_date) delete payload.dossier_preparation_planned_date;
        const updated = await updateProduct(row.id || row.product_id, payload, editorName);
        onSaved(updated);
        onToast({ type: 'ok', msg: 'Product updated successfully' });
      }
    } catch {
      onToast({ type: 'error', msg: 'Failed to update product' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Edit Product</span>
            <span className="sup-title-sub">{row.supplier_product_name || '—'}</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {detailLoading && (
            <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading product details...</div>
          )}
          {!detailLoading && isInactive && (
            <div style={{ background: '#fef2f2', color: '#991b1b', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13, border: '1px solid #fecaca' }}>
              This product was marked inactive on {fmtDateTime(row.product_deletion_date)} by {row.deleted_by || '—'}. Editing is disabled. Set Active / Inactive to Active to reactivate.
            </div>
          )}
          {!detailLoading && <div className="row g-3">
            {/* ──── Cross-Reference (read-only) ──── */}
            <CrossReferenceSection inn={row.inn} combines={row.combines_molecules} />

            {/* ──── Section 1: General Information ──── */}
            <SectionBlock bg={SECTION_TINTS.general}>
              <SectionHeading title="General Information" />
              <Field label="Supplier Product Name">
                <input className="sup-input" value={form.supplier_product_name} onChange={onChange('supplier_product_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="INN">
                <input className="sup-input" value={form.inn} onChange={onChange('inn')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Combines Molecules">
                <select className="sup-select" value={form.combines_molecules} onChange={onChange('combines_molecules')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              <Field label="Dosage Form">
                {dis ? <input className="sup-input" value={form.dosage_form} disabled style={disStyle} /> : (
                  <AutocompleteInput value={form.dosage_form} onChange={(v) => set('dosage_form', v)} suggestions={dosageForms} placeholder="Start typing…" />
                )}
              </Field>
              <Field label="Strength Value">
                <input className="sup-input" value={form.strength_value_1} onChange={onChange('strength_value_1')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Strength Unit Code">
                {dis ? <input className="sup-input" value={form.strength_unit_code_1} disabled style={disStyle} /> : (
                  <AutocompleteInput value={form.strength_unit_code_1} onChange={(v) => set('strength_unit_code_1', v)} suggestions={strengthUnits} placeholder="Start typing…" />
                )}
              </Field>
              <Field label="Pack Size">
                <input className="sup-input" value={form.pack_size} onChange={onChange('pack_size')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Reference Product">
                <input className="sup-input" value={form.reference_product_name} onChange={onChange('reference_product_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>

            {/* ──── Section 2: Market Details ──── */}
            <SectionBlock bg={SECTION_TINTS.market}>
              <SectionHeading title="Market Details" />
              <Field label="Market Auth Countries" wide>
                <input className="sup-input" value={form.market_auth_countries} onChange={onChange('market_auth_countries')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="NL Market">
                <select className="sup-select" value={form.nl_market_available} onChange={onChange('nl_market_available')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="">—</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              <Field label="NL MA Status">
                <select className="sup-select" value={form.nl_general_ma_status} onChange={onChange('nl_general_ma_status')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="">—</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              <Field label="Supplier MA Status">
                <input className="sup-input" value={form.supplier_ma_status} onChange={onChange('supplier_ma_status')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Reg. Number">
                <input className="sup-input" value={form.ma_registration_number} onChange={onChange('ma_registration_number')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Approval">
                <input className="sup-input" value={form.ma_approved_by} onChange={onChange('ma_approved_by')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Additional Info" wide>
                <input className="sup-input" value={form.ma_additional_info} onChange={onChange('ma_additional_info')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Dossier Availability Status">
                <input className="sup-input" value={form.dossier_availability_status} onChange={onChange('dossier_availability_status')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Dossier Planned Date">
                {dis ? <input className="sup-input" value={form.dossier_preparation_planned_date || '—'} disabled style={disStyle} /> : (
                  <>
                    <SearchableSelect
                      options={DOSSIER_DATE_OPTIONS}
                      value={form.dossier_preparation_planned_date}
                      onChange={(v) => { set('dossier_preparation_planned_date', v); setDossierDateTouched(true); }}
                      placeholder="Select quarter…"
                    />
                    {rawDossierDate && !dossierDateMatches && !dossierDateTouched && (
                      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3 }}>
                        Existing value: &apos;{rawDossierDate}&apos; — pick an option to overwrite, or leave unselected to keep.
                      </div>
                    )}
                  </>
                )}
              </Field>
            </SectionBlock>

            {/* ──── Section 3: ATC Classification ──── */}
            <SectionBlock bg={SECTION_TINTS.atc}>
              <SectionHeading title="ATC Classification" />
              <Field label="ATC Code">
                <input className="sup-input" value={form.atc_code} onChange={onChange('atc_code')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="ATC Classification">
                <input className="sup-input" value={form.atc_classification_name} onChange={onChange('atc_classification_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="ATC Category">
                <input className="sup-input" value={form.atc_category} onChange={onChange('atc_category')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>

            {/* ──── Section 4: Manufacturing and Patent ──── */}
            <SectionBlock bg={SECTION_TINTS.manufacturing}>
              <SectionHeading title="Manufacturing and Patent" />
              <Field label="Manufacturing Sites" wide>
                <input className="sup-input" value={form.manufacturing_sites} onChange={onChange('manufacturing_sites')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Patent Status">
                <select className="sup-select" value={form.patent_status} onChange={onChange('patent_status')} disabled={dis} style={dis ? disStyle : undefined}>
                  {PATENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Patent Expiry Date">
                {dis ? <input className="sup-input" value={form.patent_expiry_date || '—'} disabled style={disStyle} /> : (
                  <>
                    <input type="date" className="sup-input" value={isValidDate(form.patent_expiry_date) ? form.patent_expiry_date : ''} onChange={(e) => set('patent_expiry_date', e.target.value)} />
                    {form.patent_expiry_date && !isValidDate(form.patent_expiry_date) && (
                      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3 }}>
                        Existing value: &apos;{form.patent_expiry_date}&apos; — pick a date to overwrite, or leave empty to keep.
                      </div>
                    )}
                  </>
                )}
              </Field>
              <Field label="Patent Notes" wide>
                <input className="sup-input" value={form.patent_notes} onChange={onChange('patent_notes')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>

            {/* ──── Section 5: Other Details ──── */}
            <SectionBlock bg={SECTION_TINTS.other}>
              <SectionHeading title="Other Details" />
              <Field label="Therapeutical Indication" wide>
                <textarea className="sup-textarea" value={form.therapeutical_indication} onChange={onChange('therapeutical_indication')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Additional Info" wide>
                <textarea className="sup-textarea" value={form.additional_comments} onChange={onChange('additional_comments')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>

              {/* ──── Audit Fields (read-only) ──── */}
              <Field label="Creation Date">
                <input className="sup-input" value={row.created_at ? fmtDate(row.created_at) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Created From">
                <input className="sup-input" value={row.document_name_reference || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Last Import Date">
                <input className="sup-input" value={row.updated_at ? fmtDate(row.updated_at) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Last Import List">
                <input className="sup-input" value={row.document_name_reference || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Updated by">
                <input className="sup-input" value={row.created_by || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>

              {/* ──── Active / Inactive + Deletion fields ──── */}
              <Field label="Active / Inactive">
                <select
                  className="sup-select"
                  value={isInactive && !reactivationConfirmed ? 'Inactive' : 'Active'}
                  disabled={!isInactive || reactivationConfirmed}
                  style={!isInactive || reactivationConfirmed ? { color: 'var(--t3)', background: '#f8f9fa' } : undefined}
                  onChange={(e) => {
                    if (e.target.value === 'Active' && isInactive) {
                      setShowReactivateConfirm(true);
                    }
                  }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Deletion Date">
                <input className="sup-input" value={row.product_deletion_date ? fmtDateTime(row.product_deletion_date) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Deleted By">
                <input className="sup-input" value={row.deleted_by || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
            </SectionBlock>
          </div>}
        </div>
        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px', justifyContent: 'space-between' }}>
          <button type="button" className="sup-btn" onClick={() => setShowHistory(true)} style={{ marginRight: 'auto' }}>
            <i className="bi bi-clock-history me-1"></i> Historical Data
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
              <i className="bi bi-x-circle me-1"></i> {isInactive && !reactivationConfirmed ? 'Close' : 'Cancel'}
            </button>
            {(!isInactive || reactivationConfirmed) && (
              <button type="button" className="sup-btn primary" onClick={save} disabled={saving}>
                <i className="bi bi-check-circle me-1"></i> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
      {showHistory && (
        <HistoricalDataModal
          productId={row.product_id}
          moleculeName={row.molecule_name}
          inn={row.inn}
          supplierName={row.supplier_name}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showReactivateConfirm && (
        <div className="sup-modal-backdrop" style={{ zIndex: 1060 }} onClick={() => setShowReactivateConfirm(false)}>
          <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Reactivate this product?</h5>
              <button type="button" className="sup-modal-close" onClick={() => setShowReactivateConfirm(false)}>×</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              <strong>{row.supplier_product_name || '—'}</strong> (ID: {row.id || row.product_id}) will become active again and editable. The deletion record will be cleared.
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setShowReactivateConfirm(false)}>Cancel</button>
              <button type="button" className="sup-btn primary" onClick={() => { setReactivationConfirmed(true); setShowReactivateConfirm(false); }}>Yes, Reactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RemoveConfirmModal({ row, onConfirm, onCancel }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="sup-modal-backdrop" onClick={onCancel}>
      <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Remove this product?</h5>
          <button type="button" className="sup-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="sup-modal-body" style={{ fontSize: 14 }}>
          <strong>{row.supplier_product_name || '—'}</strong> (ID: {row.id || row.product_id}) will be marked as inactive. You can reactivate later from the Edit modal if needed.
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="sup-btn primary" disabled={saving} onClick={async () => {
            setSaving(true);
            await onConfirm();
            setSaving(false);
          }}>{saving ? 'Removing…' : 'Yes, Remove'}</button>
        </div>
      </div>
    </div>
  );
}

function StatusConfirmModal({ productName, newStatus, onConfirm, onCancel }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="sup-modal-backdrop" onClick={onCancel}>
      <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Confirm Status Change</h5>
          <button type="button" className="sup-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="sup-modal-body" style={{ fontSize: 14 }}>
          Are you sure you want to change the status of <strong>{productName || 'this product'}</strong> to <strong>{newStatus}</strong>?
          This update will be saved immediately.
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="sup-btn primary" disabled={saving} onClick={async () => {
            setSaving(true);
            await onConfirm();
            setSaving(false);
          }}>{saving ? 'Saving…' : 'Yes, change status'}</button>
        </div>
      </div>
    </div>
  );
}

function BulkEditModal({ count, ids, onClose, onSaved, onToast, editorName }) {
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [supplierOpts, setSupplierOpts] = useState(null);
  const [dosageOpts, setDosageOpts] = useState(null);
  const [unitOpts, setUnitOpts] = useState(null);

  useEffect(() => {
    fetchSupplierNames().then(setSupplierOpts).catch(() => setSupplierOpts([]));
    fetchDosageForms().then(setDosageOpts).catch(() => setDosageOpts([]));
    fetchStrengthUnits().then(setUnitOpts).catch(() => setUnitOpts([]));
  }, []);

  const BULK_FIELDS = useMemo(() => [
    { key: 'product_status',              label: 'Product Status',             type: 'select', options: ['Pending', 'Approved', 'Rejected'] },
    { key: 'supplier_name',               label: 'Supplier Name',              type: 'select', options: supplierOpts },
    { key: 'dosage_form',                 label: 'Dosage Form',                type: 'select', options: dosageOpts },
    { key: 'strength_unit_code_1',        label: 'Strength Unit',              type: 'select', options: unitOpts },
    { key: 'combines_molecules',          label: 'Combine Molecule',           type: 'select', options: ['Yes', 'No'] },
    { key: 'supplier_ma_status',          label: 'Supplier MA Status',         type: 'text' },
    { key: 'nl_general_ma_status',        label: 'NL Market Available',        type: 'select', options: ['Yes', 'No'] },
    { key: 'dossier_availability_status', label: 'Dossier Availability Status', type: 'text' },
  ], [supplierOpts, dosageOpts, unitOpts]);

  const toggle = (k) => setEnabled(e => ({ ...e, [k]: !e[k] }));
  const setVal = (k, v) => setValues(vals => ({ ...vals, [k]: v }));

  const save = async () => {
    const payload = {};
    for (const f of BULK_FIELDS) {
      if (enabled[f.key]) {
        const v = values[f.key] ?? '';
        if (String(v).trim() === '') continue;
        payload[f.key] = v;
      }
    }
    if (Object.keys(payload).length === 0) {
      onToast({ type: 'error', msg: 'Enable at least one field to update' });
      return;
    }
    setSaving(true);
    try {
      const res = await bulkUpdateProducts(ids, payload, editorName);
      onSaved(res);
      onToast({ type: 'ok', msg: `${res.updated_count || ids.length} products updated successfully` });
    } catch {
      onToast({ type: 'error', msg: 'Bulk update failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h3 className="sup-modal-title">Bulk Edit — {count} Products Selected</h3>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body">
          {BULK_FIELDS.map(f => {
            const optsLoading = f.type === 'select' && f.options === null;
            const optsEmpty = f.type === 'select' && Array.isArray(f.options) && f.options.length === 0;
            return (
            <div key={f.key} className="d-flex align-items-start gap-3 mb-3">
              <label className="sup-toggle mt-1">
                <input type="checkbox" checked={!!enabled[f.key]} onChange={() => toggle(f.key)} disabled={optsEmpty} />
                <span className="sup-toggle-slider" />
              </label>
              <div className="flex-grow-1">
                <div className="sup-field-label">{f.label}</div>
                {f.type === 'text' && (
                  <input
                    className="sup-input"
                    disabled={!enabled[f.key]}
                    value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                  />
                )}
                {f.type === 'select' && (
                  <SearchableSelect
                    options={f.options || []}
                    value={values[f.key] || ''}
                    onChange={(v) => setVal(f.key, v)}
                    disabled={!enabled[f.key] || optsLoading}
                    loading={optsLoading}
                    emptyLabel={optsEmpty ? 'No options available' : '—'}
                    placeholder="Type to search..."
                  />
                )}
              </div>
            </div>
            );
          })}
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button type="button" className="sup-btn primary" onClick={save} disabled={saving}>
            <i className="bi bi-check-circle me-1"></i> {saving ? 'Applying…' : 'Apply to All Selected'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Sort ---------- */

const compareBy = (key, dir) => (a, b) => {
  let av = a[key];
  let bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'desc' ? -cmp : cmp;
};

function SortHead({ label, column, sort, onSort, colWidth, onResizeStart, onAutoFit, ...rest }) {
  const active = sort.column === column;
  const icon = active ? (sort.direction === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      className="sup-sort-head"
      style={{ position: 'relative', width: colWidth || undefined, minWidth: 60, ...(rest.style || {}) }}
      {...rest}
      onClick={() => onSort(column)}
    >
      <span style={{ cursor: 'pointer', display: 'block', paddingRight: 8 }}>
        {label}
        {active && <span className="sup-sort-ico">{icon}</span>}
      </span>
      {onResizeStart && (
        <span
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart(e, column); }}
          onDoubleClick={(e) => { e.stopPropagation(); if (onAutoFit) onAutoFit(column); }}
          style={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', cursor: 'col-resize', userSelect: 'none', zIndex: 1 }}
        />
      )}
    </th>
  );
}

/* ---------- Preset Helpers ---------- */

const API = import.meta.env.VITE_API_URL;

const SP_API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';

async function getAuthHeaders(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return {};
    const resp = await msalInstance.acquireTokenSilent({
      scopes: [SP_API_SCOPE],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${resp.accessToken}` };
  } catch {
    try {
      const resp = await msalInstance.acquireTokenPopup({ scopes: [SP_API_SCOPE] });
      return { Authorization: `Bearer ${resp.accessToken}` };
    } catch {
      return {};
    }
  }
}

/* ---------- Preset Modals ---------- */

function LoadPresetModal({ title, presetType, scope, msalInstance, onApply, onClose, onToast }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const auth = await getAuthHeaders(msalInstance);
      const r = await fetch(`${API}/api/presets?type=${presetType}&scope=${encodeURIComponent(scope)}`, { headers: auth });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setPresets(data.presets || []);
    } catch {
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [msalInstance, presetType, scope]);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDelete = async (preset) => {
    try {
      const auth = await getAuthHeaders(msalInstance);
      const r = await fetch(`${API}/api/presets/${preset.id}`, { method: 'DELETE', headers: auth });
      if (!r.ok) throw new Error();
      setDeleteConfirm(null);
      fetchPresets();
      if (onToast) onToast({ type: 'ok', msg: `Preset '${preset.preset_name}' deleted.` });
    } catch {
      if (onToast) onToast({ type: 'error', msg: 'Failed to delete preset.' });
    }
  };

  const fmtUpdated = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">{title}</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sup-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--t3)' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status" />Loading...
            </div>
          )}
          {!loading && presets.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--t3)', fontSize: 13 }}>
              {presetType === 'filter'
                ? "No saved filter presets yet. Use the gear icon \u2192 'Save as new preset' to create one."
                : "No saved column presets yet. Use the save icon to create one."}
            </div>
          )}
          {!loading && presets.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eef3f7' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--navy)' }}>Preset Name</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--navy)' }}>Last Updated</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {presets.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>{p.preset_name}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--t3)', fontSize: 12 }}>{fmtUpdated(p.updated_at)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="sup-btn primary"
                        style={{ padding: '4px 12px', fontSize: 12, marginRight: 6 }}
                        onClick={() => {
                          const cfg = typeof p.config_json === 'string' ? JSON.parse(p.config_json) : p.config_json;
                          onApply(cfg, p.preset_name);
                          onClose();
                        }}
                      >Apply</button>
                      <button
                        type="button"
                        className="sup-btn"
                        style={{ padding: '4px 8px', fontSize: 12, color: '#dc2626', border: '1px solid #fecaca' }}
                        onClick={() => setDeleteConfirm(p)}
                      ><i className="bi bi-trash"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>
      {deleteConfirm && (
        <div className="sup-modal-backdrop" style={{ zIndex: 1060 }} onClick={() => setDeleteConfirm(null)}>
          <div className="sup-modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Delete preset?</h5>
              <button type="button" className="sup-modal-close" onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              Delete preset &lsquo;<strong>{deleteConfirm.preset_name}</strong>&rsquo;? This cannot be undone.
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="sup-btn primary" style={{ background: '#dc2626' }} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SavePresetModal({ title, presetType, scope, configJsonFn, msalInstance, onClose, onToast }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // { existing_id, name }

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const doSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const auth = await getAuthHeaders(msalInstance);
      const configJson = configJsonFn();
      const r = await fetch(`${API}/api/presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ preset_type: presetType, preset_scope: scope, preset_name: trimmed, config_json: configJson }),
      });
      if (r.status === 409) {
        const data = await r.json();
        setConflict({ existing_id: data.existing_id, name: trimmed });
        setSaving(false);
        return;
      }
      if (!r.ok) throw new Error();
      if (onToast) onToast({ type: 'ok', msg: `Preset '${trimmed}' saved.` });
      onClose();
    } catch {
      if (onToast) onToast({ type: 'error', msg: 'Failed to save preset.' });
    } finally {
      setSaving(false);
    }
  };

  const doReplace = async () => {
    if (!conflict) return;
    setSaving(true);
    try {
      const auth = await getAuthHeaders(msalInstance);
      const configJson = configJsonFn();
      const r = await fetch(`${API}/api/presets/${conflict.existing_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ config_json: configJson }),
      });
      if (!r.ok) throw new Error();
      if (onToast) onToast({ type: 'ok', msg: `Preset '${conflict.name}' replaced.` });
      onClose();
    } catch {
      if (onToast) onToast({ type: 'error', msg: 'Failed to replace preset.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">{title}</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sup-modal-body">
          {!conflict ? (
            <>
              <div className="sup-field-label" style={{ marginBottom: 4 }}>Preset Name</div>
              <input
                className="sup-input"
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for this preset..."
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') doSave(); }}
              />
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 8 }}>
                {presetType === 'filter'
                  ? 'Save your current filter visibility and selected values as a named preset. You can load it later from the filter preset icon.'
                  : 'Save your current column visibility as a named preset. You can load it later from the column preset icon.'}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14 }}>
              A preset named &lsquo;<strong>{conflict.name}</strong>&rsquo; already exists. Do you want to replace it?
            </div>
          )}
        </div>
        <div className="sup-modal-foot">
          {!conflict ? (
            <>
              <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="sup-btn primary" onClick={doSave} disabled={saving || !name.trim()}>
                <i className="bi bi-check-circle me-1"></i> {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="sup-btn" onClick={() => setConflict(null)} disabled={saving}>Cancel</button>
              <button type="button" className="sup-btn primary" onClick={doReplace} disabled={saving}>
                {saving ? 'Replacing...' : 'Replace'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Column definitions (label + DB key) ---------- */

const ALL_TABLE_COLUMNS = [
  { key: 'product_id', label: 'Prod ID' },
  { key: 'source_type', label: 'File Type' },
  { key: 'document_name_reference', label: 'Document Name' },
  { key: 'supplier_id', label: 'FO Supplier ID' },
  { key: 'supplier_name', label: 'Supplier' },
  { key: 'supplier_headquarter_country', label: 'Supplier HQ' },
  { key: 'manufacturing_sites', label: 'Manufacturing Sites' },
  { key: 'supplier_product_name', label: 'Product Name' },
  { key: 'product_status', label: 'Product Status' },
  { key: 'inn', label: 'INN' },
  { key: 'combines_molecules', label: 'Molecule Type' },
  { key: 'strength_value_1', label: 'Strength' },
  { key: 'strength_unit_code_1', label: 'Strength Unit' },
  { key: 'dosage_form', label: 'Dosage Form' },
  { key: 'full_strength', label: 'Full Strength' },
  { key: 'pack_size', label: 'Pack Size' },
  { key: 'reference_product_name', label: 'Reference Product' },
  { key: 'therapeutical_indication', label: 'Therapeutic Indication' },
  { key: 'atc_code', label: 'ATC Code' },
  { key: 'atc_classification_name', label: 'ATC Classification' },
  { key: 'atc_category', label: 'ATC Category' },
  { key: 'dossier_availability_status', label: 'Dossier Status' },
  { key: 'dossier_preparation_planned_date', label: 'Expected' },
  { key: 'market_auth_countries', label: 'MA Availability' },
  { key: 'market_auth_status', label: 'MA Status' },
  { key: 'supplier_ma_status', label: 'Supplier MA Status' },
  { key: 'ma_additional_info', label: 'MA Additional Info' },
  { key: 'additional_comments', label: 'Additional Comments' },
  { key: 'nl_market_available', label: 'NL MA Availability' },
  { key: 'nl_general_ma_status', label: 'NL MA Status' },
  { key: 'ma_registration_number', label: 'MA Reg. Number' },
  { key: 'ma_approved_by', label: 'MA Approval' },
  { key: 'patent_status', label: 'Patent Status' },
  { key: 'patent_expiry_date', label: 'Patent Expiry Date' },
  { key: 'patent_notes', label: 'Patent Notes' },
  { key: 'created_at', label: 'Created Date' },
  { key: 'uploaded_by', label: 'Uploaded By' },
  { key: 'created_by', label: 'Created by' },
  { key: 'updated_at', label: 'Update Date' },
  { key: 'updated_by', label: 'Updated by' },
  // Additional data columns (hidden by default for new users)
  { key: 'molecule_name', label: 'Molecule Name' },
  { key: 'molecule_comments', label: 'Molecule Comments' },
  { key: 'dosage_form_note', label: 'Dosage Form Note' },
  { key: 'therapeutic_indications', label: 'Therapeutic Indications' },
  { key: 'supplier_availability', label: 'Supplier Availability' },
  { key: 'reference_date', label: 'Reference Date' },
  { key: 'file_description', label: 'File Description' },
];
// Columns visible by default (the original set before new additions)
const LEGACY_COL_KEYS = [
  'product_id','source_type','document_name_reference','supplier_id','supplier_name',
  'supplier_headquarter_country','manufacturing_sites','supplier_product_name','product_status',
  'inn','combines_molecules','strength_value_1','strength_unit_code_1','dosage_form',
  'full_strength','pack_size','reference_product_name','therapeutical_indication',
  'atc_code','atc_classification_name','atc_category','dossier_availability_status',
  'dossier_preparation_planned_date','market_auth_countries','market_auth_status',
  'supplier_ma_status','ma_additional_info','additional_comments','nl_market_available',
  'nl_general_ma_status','ma_registration_number','ma_approved_by','patent_status',
  'patent_expiry_date','patent_notes','created_at','uploaded_by','created_by','updated_at','updated_by',
];
const ALL_COL_KEYS = ALL_TABLE_COLUMNS.map(c => c.key);
const DEFAULT_COL_ORDER = ALL_COL_KEYS.slice();

/* ---------- Page ---------- */

const DEFAULT_PAGE_SIZE = 50;

const CSV_EXPORT_COLUMNS = [
  'product_id','supplier_id','source_type','document_name_reference','product_status',
  'supplier_name','supplier_headquarter_country','supplier_product_name','inn',
  'combines_molecules','strength_value_1','strength_unit_code_1','dosage_form',
  'full_strength','pack_size','reference_product_name','atc_code',
  'atc_classification_name','atc_category','market_auth_countries','market_auth_status',
  'manufacturing_sites','therapeutical_indication','patent_expiry_date','patent_status',
  'patent_notes','nl_market_available','supplier_ma_status','nl_general_ma_status',
  'ma_registration_number','ma_approved_by','ma_additional_info',
  'dossier_availability_status','dossier_preparation_planned_date',
  'additional_comments','created_at','created_by','updated_at','updated_by','Active',
  'molecule_name','molecule_comments','dosage_form_note','therapeutic_indications',
  'supplier_availability','reference_date','file_description',
];

function csvCell(v) {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) v = v.join('; ');
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const CSV_HEADER_LABELS = { market_auth_status: 'MA Status', supplier_ma_status: 'Supplier MA Status' };

function rowsToCsv(rows) {
  const header = CSV_EXPORT_COLUMNS.map(c => CSV_HEADER_LABELS[c] || c).join(',');
  const body = rows.map(r => CSV_EXPORT_COLUMNS.map(c => {
    if (c === 'Active') return csvCell(r.product_inactive === 'Yes' ? 'Inactive' : 'Active');
    return csvCell(r[c]);
  }).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadCsvFile(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function SupplierProducts({ embedded = false, lockedInn = null } = {}) {
  const { displayName: editorName } = useUser();
  const { instance: msalInstance } = useMsal();

  // Cached API-scoped access token for /api/dashboard/* calls
  const tokenRef = useRef({ value: null, expiresAt: 0 });
  const acquireToken = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && tokenRef.current.value && tokenRef.current.expiresAt > now) {
      return tokenRef.current.value;
    }
    const t = await getDashboardToken(msalInstance);
    if (t) {
      // Cache for 50 min (tokens typically last ~1h, MSAL handles the rest)
      tokenRef.current = { value: t, expiresAt: now + 50 * 60 * 1000 };
    }
    return t;
  }, [msalInstance]);

  const [rows, setRows]       = useState(null);
  const [serverNl, setServerNl] = useState(0);
  const [err, setErr]         = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [editRow, setEditRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toast, setToast]     = useState(null);
  const [sort, setSort]       = useState({ column: null, direction: 'asc' });
  const [filters, setFilters] = useState({ supplier: [], product: [], form: [], type: [], dossier: [], ma: [], maCountries: '', patent: [], dossierAvail: [], dossierPrepDate: '', euAvail: [], hqCountry: [], productName: [], strength: [], unit: [], referenceProduct: [], atcCode: [], atcClassification: [], atcCategory: [], marketAuthStatus: [], therapeuticalIndication: [], patentExpiryDate: [], patentNotes: [], nlMaStatus: [], productId: '', productStatus: [] });
  const [csvOpen, setCsvOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dosageForms, setDosageForms]     = useState([]);
  const [strengthUnits, setStrengthUnits] = useState([]);
  const [hqCountryOptions, setHqCountryOptions] = useState([]);
  // Full supplier directory (f1_supplier_list) — so suppliers with no products yet are still selectable
  const [allSupplierNames, setAllSupplierNames] = useState([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showFilterSettings, setShowFilterSettings] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [externalCounts, setExternalCounts] = useState(null);
  const [externalLoading, setExternalLoading] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = useRef({ key: null, startX: 0, startWidth: 0 });
  const saveWidthsTimer = useRef(null);
  const downloadRef = useRef(null);
  const gearRef = useRef(null);
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const [loadFilterPresetOpen, setLoadFilterPresetOpen] = useState(false);
  const [loadColumnPresetOpen, setLoadColumnPresetOpen] = useState(false);
  const [saveColumnPresetOpen, setSaveColumnPresetOpen] = useState(false);
  const [saveFilterPresetOpen, setSaveFilterPresetOpen] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(LEGACY_COL_KEYS));
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const columnManagerRef = useRef(null);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COL_ORDER);
  const saveOrderTimer = useRef(null);
  const [panelDragIdx, setPanelDragIdx] = useState(null);
  const [uploadResultModal, setUploadResultModal] = useState(null);

  // Upload tracking context — reopen result modal if processing finishes while on this page
  const { pendingResult, clearResult } = useUploadTracking();
  useEffect(() => {
    if (pendingResult) {
      setUploadResultModal(pendingResult);
      clearResult();
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingResult]);

  const AVAILABLE_FILTERS = useMemo(() => [
    { key: 'productId', label: 'Prod ID' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'inn', label: 'INN' },
    { key: 'form', label: 'Dosage Form' },
    { key: 'type', label: 'Molecule Type' },
    { key: 'maStatus', label: 'MA Status' },
    { key: 'marketAuthCountries', label: 'MA Availability' },
    { key: 'patentStatus', label: 'Patent Status' },
    { key: 'dossierAvailability', label: 'Dossier Status' },
    { key: 'dossierPrepDate', label: 'Expected' },
    { key: 'euMarketAvailability', label: 'EU Market Availability' },
    { key: 'hqCountry', label: 'Supplier HQ' },
    { key: 'productName', label: 'Product Name' },
    { key: 'strength', label: 'Strength' },
    { key: 'unit', label: 'Strength Unit' },
    { key: 'referenceProduct', label: 'Reference Product' },
    { key: 'atcCode', label: 'ATC Code' },
    { key: 'atcClassification', label: 'ATC Classification' },
    { key: 'atcCategory', label: 'ATC Category' },
    { key: 'marketAuthStatus', label: 'Supplier MA Status' },
    { key: 'therapeuticalIndication', label: 'Therapeutic Indication' },
    { key: 'patentExpiryDate', label: 'Patent Expiry Date' },
    { key: 'patentNotes', label: 'Patent Notes' },
    { key: 'nlMaStatus', label: 'NL MA Status' },
  ], []);

  const ALL_FILTER_KEYS = useMemo(() => AVAILABLE_FILTERS.map(f => f.key), [AVAILABLE_FILTERS]);

  const [visibleFilters, setVisibleFilters] = useState(() => AVAILABLE_FILTERS.map(f => f.key));
  const [pendingFilters, setPendingFilters] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm } = useSearch();

  // ── URL param ↔ filter state mapping ──
  // Each entry: [urlParam, filterStateKey, isArray]
  const FILTER_URL_MAP = useMemo(() => [
    ['prod_id',              'productId',              false],
    ['supplier',             'supplier',               true],
    ['inn',                  'product',                true],
    ['dosage_form',          'form',                   true],
    ['molecule_type',        'type',                   true],
    ['ma_status',            'ma',                     true],
    ['ma_availability',      'maCountries',            false],
    ['patent_status',        'patent',                 true],
    ['dossier_status',       'dossierAvail',           true],
    ['dossier_prep_date',    'dossierPrepDate',        false],
    ['eu_availability',      'euAvail',                true],
    ['hq_country',           'hqCountry',              true],
    ['product_name',         'productName',            true],
    ['strength',             'strength',               true],
    ['strength_unit',        'unit',                   true],
    ['reference_product',    'referenceProduct',       true],
    ['atc_code',             'atcCode',                true],
    ['atc_classification',   'atcClassification',      true],
    ['atc_category',         'atcCategory',            true],
    ['market_auth_status',   'marketAuthStatus',       true],
    ['therapeutic_indication','therapeuticalIndication', true],
    ['patent_expiry_date',   'patentExpiryDate',       true],
    ['patent_notes',         'patentNotes',            true],
    ['nl_ma_status',         'nlMaStatus',             true],
    ['product_status',       'productStatus',          true],
  ], []);

  const EMPTY_FILTERS = useMemo(() => ({ supplier: [], product: [], form: [], type: [], dossier: [], ma: [], maCountries: '', patent: [], dossierAvail: [], dossierPrepDate: '', euAvail: [], hqCountry: [], productName: [], strength: [], unit: [], referenceProduct: [], atcCode: [], atcClassification: [], atcCategory: [], marketAuthStatus: [], therapeuticalIndication: [], patentExpiryDate: [], patentNotes: [], nlMaStatus: [], productId: '', productStatus: [] }), []);

  // Build URL search string from current filter state
  const buildUrlParams = useCallback((f) => {
    const p = new URLSearchParams();
    for (const [urlKey, stateKey, isArr] of FILTER_URL_MAP) {
      const val = f[stateKey];
      if (isArr) {
        if (val && val.length > 0) p.set(urlKey, val.join('|'));
      } else {
        if (val) p.set(urlKey, val);
      }
    }
    return p.toString();
  }, [FILTER_URL_MAP]);

  // Push filter state to URL (replace, no history entry per keystroke)
  const pushFiltersToUrl = useCallback((f) => {
    if (embedded) return; // embedded in cockpit — never navigate away
    const qs = buildUrlParams(f);
    navigate(qs ? `/supplier-products?${qs}` : '/supplier-products', { replace: true });
  }, [buildUrlParams, navigate, embedded]);

  // Wrapper: update filter state AND sync to URL
  const updateFilters = useCallback((updater) => {
    setFilters(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Defer URL push to avoid calling navigate during render
      queueMicrotask(() => pushFiltersToUrl(next));
      return next;
    });
  }, [pushFiltersToUrl]);

  // On URL change (including first load): read URL → reset filters → apply
  // Skip if no URL params (bare visit falls back to user-prefs / current state)
  useEffect(() => {
    if (embedded) return; // embedded mode ignores the host route's URL params
    const params = new URLSearchParams(location.search);
    if (params.toString() === '') return;
    // URL has filter params → they become source of truth
    const next = { ...EMPTY_FILTERS };
    for (const [urlKey, stateKey, isArr] of FILTER_URL_MAP) {
      const raw = params.get(urlKey);
      if (raw) {
        next[stateKey] = isArr ? raw.split('|') : raw;
      }
    }
    setFilters(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Nova sessionStorage filter — convert to URL params then navigate
  useEffect(() => {
    if (embedded) return; // embedded mode ignores Nova sessionStorage filters
    const handleNovaFilter = () => {
      const raw = sessionStorage.getItem('novaFilter');
      if (!raw) return;
      try {
        const f = JSON.parse(raw);
        const next = { ...EMPTY_FILTERS };
        if (f.supplier) next.supplier = [f.supplier];
        if (f.form) next.form = [f.form];
        if (f.ma) next.ma = [f.ma];
        if (f.dossier) next.dossierAvail = [f.dossier];
        if (f.search) next.product = [f.search];
        pushFiltersToUrl(next);
      } catch { /* ignore */ }
      sessionStorage.removeItem('novaFilter');
    };
    handleNovaFilter();
    window.addEventListener('novaFilterUpdate', handleNovaFilter);
    return () => window.removeEventListener('novaFilterUpdate', handleNovaFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setRows(null); setErr('');
    try {
      let token = await acquireToken();
      let data;
      try {
        data = await fetchAllSupplierProducts({}, token);
      } catch (e) {
        if (e?.response?.status === 401) {
          token = await acquireToken(true);
          data = await fetchAllSupplierProducts({}, token);
        } else throw e;
      }
      const rawRows = Array.isArray(data.rows) ? data.rows : [];
      // Ensure every row has a unique id for checkbox selection
      const seen = new Set();
      const ensured = rawRows.map((r, idx) => {
        let uid = r.id;
        if (uid == null || seen.has(uid)) uid = `_row_${idx}`;
        seen.add(uid);
        return uid === r.id ? r : { ...r, id: uid };
      });
      setRows(ensured);
      setServerNl(data.nlCount || 0);
    } catch {
      setErr('Failed to load supplier products.');
    }
  }, [acquireToken]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load(); }, [load]);

  // Load the full supplier directory so new/product-less suppliers appear in the Supplier filter
  useEffect(() => {
    fetchSupplierNames().then((n) => setAllSupplierNames(Array.isArray(n) ? n : [])).catch(() => setAllSupplierNames([]));
  }, []);

  // Switch status (red/orange/green) for each molecule — uses POST to handle 2000+ INNs
  const [switchStatusMap, setSwitchStatusMap] = useState({});
  useEffect(() => {
    if (!rows || !rows.length) return;
    const mols = [...new Set(rows.map(r => (r.inn || '').toUpperCase().trim()).filter(Boolean))];
    if (!mols.length) return;
    (async () => {
      try {
        const token = await acquireToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/market-intelligence/switch-status`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ molecules: mols }),
        });
        if (res.ok) {
          const raw = await res.json();
          const normalized = {};
          for (const [k, v] of Object.entries(raw)) normalized[k.toUpperCase().trim()] = v;
          setSwitchStatusMap(normalized);
        }
      } catch { /* ignore */ }
    })();
  }, [rows, acquireToken]);

  useEffect(() => {
    fetchDosageForms().then(setDosageForms).catch(() => {});
    fetchStrengthUnits().then(setStrengthUnits).catch(() => {});
    fetchHqCountries().then(setHqCountryOptions).catch(() => {});
  }, []);

  // Close download dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
      if (gearRef.current && !gearRef.current.contains(e.target)) {
        setShowFilterSettings(false);
        setPendingFilters(null);
      }
      if (columnManagerRef.current && !columnManagerRef.current.contains(e.target)) {
        setShowColumnManager(false);
        setColumnSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load filter preferences on every page visit
  useEffect(() => {
    // Resolve user identifier
    let userId = null;
    try {
      // MSAL accounts may be available via context in the future
    } catch { /* ignore */ }
    if (!userId) {
      if (!localStorage.getItem('focusone_uid')) {
        localStorage.setItem('focusone_uid', 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
      }
      userId = localStorage.getItem('focusone_uid');
    }
    setCurrentUserId(userId);

    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(userId)}/supplierProductFilters`)
      .then(r => {
        if (!r.ok) throw new Error('No preferences found');
        return r.json();
      })
      .then(data => {
        if (data && data.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (Array.isArray(saved) && saved.length > 0) {
            setVisibleFilters(saved);
            return;
          }
        }
        // No saved preference — show ALL filters
        setVisibleFilters(ALL_FILTER_KEYS);
      })
      .catch(() => {
        setVisibleFilters(ALL_FILTER_KEYS);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const saveFilterPreferences = async () => {
    if (!pendingFilters || !currentUserId) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: currentUserId,
          key: 'supplierProductFilters',
          value: JSON.stringify(pendingFilters),
        }),
      });
      if (!response.ok) throw new Error('Save failed');
      setVisibleFilters(pendingFilters);
      setPendingFilters(null);
      setShowFilterSettings(false);
    } catch {
      setToast({ type: 'error', msg: 'Failed to save filter preferences. Please try again.' });
    }
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Fetch external register counts when INN filter changes
  useEffect(() => {
    const activeINNs = filters.product;
    if (!activeINNs || activeINNs.length === 0) {
      setExternalCounts(null);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setExternalLoading(true);
      const params = new URLSearchParams();
      activeINNs.forEach(i => params.append('inn', i));
      fetch(`${import.meta.env.VITE_API_URL}/api/cross-reference/summary?${params}`, { signal: ac.signal })
        .then(r => r.json())
        .then(d => { if (!ac.signal.aborted) setExternalCounts(d); })
        .catch(err => { if (!ac.signal.aborted) { console.error('External counts fetch failed:', err); setExternalCounts(null); } })
        .finally(() => { if (!ac.signal.aborted) setExternalLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); ac.abort(); };
  }, [filters.product]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPage(1); }, [filters, sort, searchTerm]);

  const rowsSafe = useMemo(() => {
    const base = rows || [];
    if (!lockedInn) return base;
    const key = String(lockedInn).toUpperCase().trim();
    return base.filter((r) =>
      String(r.inn || '').toUpperCase().trim() === key ||
      String(r.molecule_name || '').toUpperCase().trim() === key);
  }, [rows, lockedInn]);

  // ── Cascading (Excel-style) filter options ──
  // Each filter's options are derived from rows matching ALL OTHER active filters,
  // so options narrow as the user applies more filters.
  const cascadeOptions = useMemo(() => {
    // Individual filter predicates keyed by filter state key
    const predicates = {
      supplier: r => filters.supplier.length === 0 || filters.supplier.includes(String(r.supplier_name || '')),
      product: r => filters.product.length === 0 || (() => {
        const inn = String(r.inn || '').toLowerCase();
        const mol = String(r.molecule_name || '').toLowerCase();
        const pn = String(r.supplier_product_name || '').toLowerCase();
        return filters.product.some(t => { const tl = String(t).toLowerCase(); return inn.includes(tl) || mol.includes(tl) || pn.includes(tl); });
      })(),
      form: r => filters.form.length === 0 || filters.form.includes(String(r.dosage_form || '')),
      type: r => filters.type.length === 0 || (() => {
        const combined = String(r.combines_molecules || '').toLowerCase() === 'yes';
        return filters.type.includes(combined ? 'Combined' : 'Single');
      })(),
      ma: r => filters.ma.length === 0 || filters.ma.includes(String(r.market_auth_status || r.ma_status || '')),
      patent: r => filters.patent.length === 0 || filters.patent.includes(String(r.patent_status || '')),
      dossierAvail: r => filters.dossierAvail.length === 0 || filters.dossierAvail.includes(String(r.dossier_availability_status || '')),
      hqCountry: r => filters.hqCountry.length === 0 || filters.hqCountry.includes(String(r.supplier_headquarter_country || r.hq_country || '')),
      productName: r => filters.productName.length === 0 || filters.productName.includes(String(r.supplier_product_name || '')),
      strength: r => filters.strength.length === 0 || filters.strength.includes(String(r.strength_value_1 ?? '')),
      unit: r => filters.unit.length === 0 || filters.unit.includes(String(r.strength_unit_code_1 || '')),
      referenceProduct: r => filters.referenceProduct.length === 0 || filters.referenceProduct.includes(String(r.reference_product_name || '')),
      atcCode: r => filters.atcCode.length === 0 || (() => {
        const code = String(r.atc_code || '').toLowerCase();
        return filters.atcCode.some(f => code.startsWith(String(f).toLowerCase()));
      })(),
      atcClassification: r => filters.atcClassification.length === 0 || (() => {
        const cls = String(r.atc_classification_name || '').toLowerCase();
        return filters.atcClassification.some(f => cls.includes(String(f).toLowerCase()));
      })(),
      atcCategory: r => filters.atcCategory.length === 0 || (() => {
        const cat = String(r.atc_category || '').toLowerCase();
        return filters.atcCategory.some(f => cat.includes(String(f).toLowerCase()));
      })(),
      marketAuthStatus: r => filters.marketAuthStatus.length === 0 || filters.marketAuthStatus.includes(String(r.market_auth_status || '')),
      therapeuticalIndication: r => filters.therapeuticalIndication.length === 0 || filters.therapeuticalIndication.includes(String(r.therapeutical_indication || '')),
      patentExpiryDate: r => filters.patentExpiryDate.length === 0 || filters.patentExpiryDate.includes(String(r.patent_expiry_date || '')),
      patentNotes: r => filters.patentNotes.length === 0 || filters.patentNotes.includes(String(r.patent_notes || '')),
      nlMaStatus: r => filters.nlMaStatus.length === 0 || filters.nlMaStatus.includes(String(r.nl_general_ma_status || '')),
      productStatus: r => !(filters.productStatus && filters.productStatus.length > 0) || (() => {
        const raw = r.product_status;
        const isEmpty = !raw || !String(raw).trim();
        if (filters.productStatus.includes('No Status') && isEmpty) return true;
        if (isEmpty) return false;
        return filters.productStatus.includes(normalizeStatus(raw));
      })(),
      euAvail: r => filters.euAvail.length === 0 || filters.euAvail.includes(euAvailability(r.market_auth_countries)),
    };
    const predKeys = Object.keys(predicates);

    // Filter rows by all predicates EXCEPT the excluded one
    function rowsExcluding(excludeKey) {
      return rowsSafe.filter(r => predKeys.every(k => k === excludeKey || predicates[k](r)));
    }

    const uv = (rows, key) => uniqueValues(rows, key);

    const supplierSubset = rowsExcluding('supplier');
    const productSubset = rowsExcluding('product');
    const formSubset = rowsExcluding('form');
    const maSubset = rowsExcluding('ma');
    const patentSubset = rowsExcluding('patent');
    const dossierSubset = rowsExcluding('dossierAvail');
    const hqSubset = rowsExcluding('hqCountry');

    return {
      supplier: Array.from(new Set([...uv(supplierSubset, 'supplier_name'), ...allSupplierNames]))
        .filter((s) => s && String(s).trim()).sort((a, b) => a.localeCompare(b)),
      product: (() => { const set = new Set(); for (const r of productSubset) { if (r.inn && String(r.inn).trim()) set.add(String(r.inn)); if (r.molecule_name && String(r.molecule_name).trim()) set.add(String(r.molecule_name)); } return Array.from(set).sort((a,b) => a.localeCompare(b)); })(),
      form: uv(formSubset, 'dosage_form'),
      ma: (() => { const set = new Set(); maSubset.forEach(r => { const v = r.market_auth_status || r.ma_status; if (v) set.add(String(v)); }); return Array.from(set).sort((a,b) => a.localeCompare(b)); })(),
      patent: uv(patentSubset, 'patent_status'),
      dossierAvail: uv(dossierSubset, 'dossier_availability_status'),
      hqCountry: uv(hqSubset, 'supplier_headquarter_country'),
      productName: uv(rowsExcluding('productName'), 'supplier_product_name'),
      strength: uv(rowsExcluding('strength'), 'strength_value_1'),
      unit: uv(rowsExcluding('unit'), 'strength_unit_code_1'),
      referenceProduct: uv(rowsExcluding('referenceProduct'), 'reference_product_name'),
      atcCode: uv(rowsExcluding('atcCode'), 'atc_code'),
      atcClassification: uv(rowsExcluding('atcClassification'), 'atc_classification_name'),
      atcCategory: uv(rowsExcluding('atcCategory'), 'atc_category'),
      marketAuthStatus: uv(rowsExcluding('marketAuthStatus'), 'market_auth_status'),
      therapeuticalIndication: uv(rowsExcluding('therapeuticalIndication'), 'therapeutical_indication'),
      patentExpiryDate: uv(rowsExcluding('patentExpiryDate'), 'patent_expiry_date'),
      patentNotes: uv(rowsExcluding('patentNotes'), 'patent_notes'),
      nlMaStatus: uv(rowsExcluding('nlMaStatus'), 'nl_general_ma_status'),
    };
  }, [rowsSafe, filters, allSupplierNames]);

  const supplierOptions = cascadeOptions.supplier;
  const productOptions = cascadeOptions.product;
  const formOptions = cascadeOptions.form;
  const maOptions = cascadeOptions.ma;
  const patentOptions = cascadeOptions.patent;
  const dossierOptions = cascadeOptions.dossierAvail;
  const dossierAvailOpt = cascadeOptions.dossierAvail;
  const hqCountryEffective = cascadeOptions.hqCountry;
  const productNameOptions = cascadeOptions.productName;
  const strengthOptions = cascadeOptions.strength;
  const unitOptions = cascadeOptions.unit;
  const referenceProductOptions = cascadeOptions.referenceProduct;
  const atcCodeOptions = cascadeOptions.atcCode;
  const atcClassificationOptions = cascadeOptions.atcClassification;
  const atcCategoryOptions = cascadeOptions.atcCategory;
  const marketAuthStatusOptions = cascadeOptions.marketAuthStatus;
  const therIndOptions = cascadeOptions.therapeuticalIndication;
  const patentExpiryOptions = cascadeOptions.patentExpiryDate;
  const patentNotesOptions = cascadeOptions.patentNotes;
  const nlMaStatusOptions = cascadeOptions.nlMaStatus;

  // Dual scrollbar sync
  const syncFromTop = useCallback(() => {
    if (tableScrollRef.current && topScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  }, []);
  const syncFromTable = useCallback(() => {
    if (tableScrollRef.current && topScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  }, []);

  // Column resize handlers
  const onResizing = useCallback((e) => {
    const { key, startX, startWidth } = resizeRef.current;
    if (!key) return;
    const next = Math.max(60, startWidth + (e.clientX - startX));
    setColumnWidths(w => ({ ...w, [key]: next }));
  }, []);

  const saveColumnWidths = useCallback((widths) => {
    if (!currentUserId) return;
    clearTimeout(saveWidthsTimer.current);
    saveWidthsTimer.current = setTimeout(() => {
      fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: currentUserId, key: 'supplierProductColumnWidths', value: JSON.stringify(widths) }),
      }).catch(() => {});
    }, 300);
  }, [currentUserId]);

  const stopResize = useCallback(() => {
    resizeRef.current = { key: null, startX: 0, startWidth: 0 };
    window.removeEventListener('mousemove', onResizing);
    window.removeEventListener('mouseup', stopResize);
    setColumnWidths(w => { saveColumnWidths(w); return w; });
  }, [onResizing, saveColumnWidths]);

  const startResize = useCallback((e, key) => {
    const th = e.currentTarget.parentElement;
    resizeRef.current = { key, startX: e.clientX, startWidth: th.getBoundingClientRect().width };
    window.addEventListener('mousemove', onResizing);
    window.addEventListener('mouseup', stopResize);
  }, [onResizing, stopResize]);

  const autoFitColumn = useCallback((key) => {
    if (!tableScrollRef.current) return;
    const table = tableScrollRef.current.querySelector('table');
    if (!table) return;
    const headers = table.querySelectorAll('th');
    let colIdx = -1;
    headers.forEach((th, i) => { if (th.dataset.col === key) colIdx = i; });
    if (colIdx < 0) return;
    let maxW = 60;
    table.querySelectorAll('tbody tr').forEach(tr => {
      const td = tr.children[colIdx];
      if (td) maxW = Math.max(maxW, td.scrollWidth + 16);
    });
    maxW = Math.min(maxW, 600);
    setColumnWidths(w => { const n = { ...w, [key]: maxW }; saveColumnWidths(n); return n; });
  }, [saveColumnWidths]);

  // Load column width preferences
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(currentUserId)}/supplierProductColumnWidths`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data && data.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (saved && typeof saved === 'object') setColumnWidths(saved);
        }
      })
      .catch(() => {});
  }, [currentUserId]);

  // Load column order preferences
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(currentUserId)}/supplierProductColumnOrder`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data && data.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (Array.isArray(saved) && saved.length > 0) {
            // Merge: use saved order, append any new columns not in saved
            const savedSet = new Set(saved);
            const validSaved = saved.filter(k => ALL_COL_KEYS.includes(k));
            const missing = ALL_COL_KEYS.filter(k => !savedSet.has(k));
            setColumnOrder([...validSaved, ...missing]);
          }
        }
      })
      .catch(() => {});
  }, [currentUserId]);

  const saveColumnOrder = useCallback((order) => {
    if (!currentUserId) return;
    clearTimeout(saveOrderTimer.current);
    saveOrderTimer.current = setTimeout(() => {
      fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: currentUserId, key: 'supplierProductColumnOrder', value: JSON.stringify(order) }),
      }).catch(() => {
        setToast({ type: 'error', msg: 'Could not save column order \u2014 change is local only until next refresh.' });
      });
    }, 500);
  }, [currentUserId]);

  const handleColumnDrop = useCallback((dragKey, dropKey) => {
    if (dragKey === dropKey) return;
    setColumnOrder(prev => {
      const next = prev.filter(k => k !== dragKey);
      const dropIdx = next.indexOf(dropKey);
      if (dropIdx < 0) return prev;
      next.splice(dropIdx, 0, dragKey);
      saveColumnOrder(next);
      return next;
    });
  }, [saveColumnOrder]);

  // Ordered columns for rendering (only visible ones, in user order)
  const orderedColumns = useMemo(() => {
    const colMap = {};
    for (const c of ALL_TABLE_COLUMNS) colMap[c.key] = c;
    return columnOrder.filter(k => colMap[k]).map(k => colMap[k]);
  }, [columnOrder]);

  // CSS to hide columns not in visibleColumns set
  const columnHideCss = useMemo(() => {
    if (visibleColumns.size === ALL_TABLE_COLUMNS.length) return '';
    const rules = [];
    orderedColumns.forEach((col, idx) => {
      if (!visibleColumns.has(col.key)) {
        const nth = idx + 5; // offset by 4 fixed columns (checkbox, open, remove, active)
        rules.push(`.sup-table th:nth-child(${nth}),.sup-table td:nth-child(${nth}){display:none}`);
      }
    });
    return rules.join('\n');
  }, [visibleColumns, orderedColumns]);

  const filtered = useMemo(() => {
    let arr = rowsSafe;
    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter(r => {
        const supplier = String(r.supplier_name || '').toLowerCase();
        const name = String(r.supplier_product_name || '').toLowerCase();
        const inn  = String(r.inn || '').toLowerCase();
        const mol  = String(r.molecule_name || '').toLowerCase();
        const form = String(r.dosage_form || '').toLowerCase();
        return supplier.includes(q) || name.includes(q) || inn.includes(q)
          || mol.includes(q) || form.includes(q);
      });
    }
    const pidQ = (filters.productId || '').trim();
    if (pidQ) {
      arr = arr.filter(r => String(r.product_id || '').startsWith(pidQ));
    }
    if (filters.supplier.length > 0) {
      arr = arr.filter(r => filters.supplier.includes(String(r.supplier_name || '')));
    }
    // INN / Molecule / Product filter — multi-select pills
    if (filters.product.length > 0) {
      arr = arr.filter(r => {
        const inn = String(r.inn || '').toLowerCase();
        const mol = String(r.molecule_name || '').toLowerCase();
        const pname = String(r.supplier_product_name || '').toLowerCase();
        return filters.product.some(term => {
          const t = String(term).toLowerCase();
          return inn.includes(t) || mol.includes(t) || pname.includes(t);
        });
      });
    }
    if (filters.form.length > 0) {
      arr = arr.filter(r => filters.form.includes(String(r.dosage_form || '')));
    }
    if (filters.type.length > 0) {
      arr = arr.filter(r => {
        const combined = String(r.combines_molecules || '').toLowerCase() === 'yes';
        const label = combined ? 'Combined' : 'Single';
        return filters.type.includes(label);
      });
    }
    if (filters.dossier.length > 0) {
      arr = arr.filter(r => filters.dossier.includes(String(r.dossier_availability_status || '')));
    }
    if (filters.ma.length > 0) {
      arr = arr.filter(r => filters.ma.includes(String(r.market_auth_status || r.ma_status || '')));
    }
    // Row 2 filters
    const macQ = (filters.maCountries || '').trim().toLowerCase();
    if (macQ) {
      arr = arr.filter(r => String(r.market_auth_countries || '').toLowerCase().includes(macQ));
    }
    if (filters.patent.length > 0) {
      arr = arr.filter(r => filters.patent.includes(String(r.patent_status || '')));
    }
    if (filters.dossierAvail.length > 0) {
      arr = arr.filter(r => filters.dossierAvail.includes(String(r.dossier_availability_status || '')));
    }
    const dpQ = (filters.dossierPrepDate || '').trim().toLowerCase();
    if (dpQ) {
      arr = arr.filter(r => String(r.dossier_preparation_planned_date || '').toLowerCase().includes(dpQ));
    }
    if (filters.euAvail.length > 0) {
      arr = arr.filter(r => {
        const eu = euAvailability(r.market_auth_countries);
        return filters.euAvail.includes(eu);
      });
    }
    if (filters.hqCountry.length > 0) {
      arr = arr.filter(r => filters.hqCountry.includes(String(r.supplier_headquarter_country || r.hq_country || '')));
    }
    if (filters.productName.length > 0) {
      arr = arr.filter(r => filters.productName.includes(String(r.supplier_product_name || '')));
    }
    if (filters.strength.length > 0) {
      arr = arr.filter(r => filters.strength.includes(String(r.strength_value_1 ?? '')));
    }
    if (filters.unit.length > 0) {
      arr = arr.filter(r => filters.unit.includes(String(r.strength_unit_code_1 || '')));
    }
    if (filters.referenceProduct.length > 0) {
      arr = arr.filter(r => filters.referenceProduct.includes(String(r.reference_product_name || '')));
    }
    if (filters.atcCode.length > 0) {
      arr = arr.filter(r => {
        const code = String(r.atc_code || '').toLowerCase();
        return filters.atcCode.some(f => code.startsWith(String(f).toLowerCase()));
      });
    }
    if (filters.atcClassification.length > 0) {
      arr = arr.filter(r => {
        const cls = String(r.atc_classification_name || '').toLowerCase();
        return filters.atcClassification.some(f => cls.includes(String(f).toLowerCase()));
      });
    }
    if (filters.atcCategory.length > 0) {
      arr = arr.filter(r => {
        const cat = String(r.atc_category || '').toLowerCase();
        return filters.atcCategory.some(f => cat.includes(String(f).toLowerCase()));
      });
    }
    if (filters.marketAuthStatus.length > 0) {
      arr = arr.filter(r => filters.marketAuthStatus.includes(String(r.market_auth_status || '')));
    }
    if (filters.therapeuticalIndication.length > 0) {
      arr = arr.filter(r => filters.therapeuticalIndication.includes(String(r.therapeutical_indication || '')));
    }
    if (filters.patentExpiryDate.length > 0) {
      arr = arr.filter(r => filters.patentExpiryDate.includes(String(r.patent_expiry_date || '')));
    }
    if (filters.patentNotes.length > 0) {
      arr = arr.filter(r => filters.patentNotes.includes(String(r.patent_notes || '')));
    }
    if (filters.nlMaStatus.length > 0) {
      arr = arr.filter(r => filters.nlMaStatus.includes(String(r.nl_general_ma_status || '')));
    }
    if (filters.productStatus && filters.productStatus.length > 0) {
      arr = arr.filter(r => {
        const raw = r.product_status;
        const isEmpty = !raw || !String(raw).trim();
        if (filters.productStatus.includes('No Status') && isEmpty) return true;
        if (isEmpty) return false;
        return filters.productStatus.includes(normalizeStatus(raw));
      });
    }
    return arr;
  }, [rowsSafe, searchTerm, filters]);

  const sorted = useMemo(() => {
    if (!sort.column) return filtered;
    return [...filtered].sort(compareBy(sort.column, sort.direction));
  }, [filtered, sort]);

  const totalCount = rowsSafe.length;
  const nlCount = useMemo(
    () => (rowsSafe.length ? rowsSafe.filter(r => isNL(r.market_auth_countries)).length : serverNl),
    [rowsSafe, serverNl]
  );

  const distinctSupplierCount = useMemo(() => {
    const set = new Set();
    for (const r of filtered) {
      const s = r.supplier_name;
      if (s && r.product_inactive !== 'Yes') set.add(s);
    }
    return set.size;
  }, [filtered]);

  const effectivePageSize = Math.max(1, pageSize || DEFAULT_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const safePage  = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * effectivePageSize;
  const pageEnd   = Math.min(pageStart + effectivePageSize, sorted.length);
  const pageRows  = sorted.slice(pageStart, pageEnd);

  // Sync top scrollbar width after pageRows changes
  useEffect(() => {
    if (tableScrollRef.current && topScrollRef.current && topScrollRef.current.firstChild) {
      topScrollRef.current.firstChild.style.width = tableScrollRef.current.scrollWidth + 'px';
    }
  }, [pageRows, pageSize]);

  const getDownloadRows = () => {
    const selectedRows = sorted.filter(r => selected.has(r.id));
    return selectedRows.length > 0 ? selectedRows : sorted;
  };

  const handleDownloadCSV = () => {
    const exportRows = getDownloadRows();
    if (exportRows.length === 0) {
      setToast({ type: 'error', msg: 'No rows to download' });
      return;
    }
    downloadCsvFile(`all_supplier_products_${todayStamp()}.csv`, rowsToCsv(exportRows));
  };

  const handleDownloadCSVRef = useRef(handleDownloadCSV);
  handleDownloadCSVRef.current = handleDownloadCSV;

  // Listen for Nova chat "Export CSV" chip trigger
  useEffect(() => {
    const handler = () => handleDownloadCSVRef.current();
    window.addEventListener('novaTriggerCsvExport', handler);
    return () => window.removeEventListener('novaTriggerCsvExport', handler);
  }, []);

  const handleDownloadPDF = () => {
    const rows = getDownloadRows();
    if (rows.length === 0) {
      setToast({ type: 'error', msg: 'No rows to download' });
      return;
    }
    const esc = (c) => String(c).replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const win = window.open('', '_blank');
    win.document.write(`<html>
<head>
  <title>FocusOne - Supplier Products</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #333; }
    .header {
      display: flex; align-items: center; gap: 16px;
      padding: 16px 20px; border-bottom: 3px solid #0D2B3E;
      margin-bottom: 16px; background: #0D2B3E;
    }
    .logo-circle {
      width: 44px; height: 44px; background: #1A8AAF; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 20px; font-weight: 900; color: white; flex-shrink: 0;
    }
    .header-text { color: white; }
    .header-title { font-size: 18px; font-weight: 700; letter-spacing: 0.5px; }
    .header-subtitle { font-size: 11px; opacity: 0.8; margin-top: 2px; }
    .meta { padding: 0 20px 12px; color: #666; font-size: 11px; display: flex; gap: 24px; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    th { background: #0D2B3E; color: white; padding: 6px 8px; text-align: left; font-weight: 600; white-space: nowrap; }
    td { padding: 5px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    tr:nth-child(even) td { background: #F7FAFD; }
    @media print {
      .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr:nth-child(even) td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
<div class="header">
  <div class="logo-circle">F</div>
  <div class="header-text">
    <div class="header-title">FOCUS ONE</div>
    <div class="header-subtitle">Market Intelligence Platform</div>
  </div>
</div>
<div class="meta">
  <span><strong>Report:</strong> All Supplier Products</span>
  <span><strong>Date:</strong> ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
  <span><strong>Total Records:</strong> ${rows.length}</span>
</div>`);
    const headers = ['Supplier','Product','INN','Type','Form','Strength','Unit','NL','Market Auth Countries','MA Status','Patent Status','Dossier','Therapeutical Indication'];
    win.document.write('<table><thead><tr>' + headers.map(h => '<th>' + h + '</th>').join('') + '</tr></thead><tbody>');
    rows.forEach(row => {
      const cells = [
        row.supplier_name || '',
        row.supplier_product_name || '',
        row.inn || '',
        String(row.combines_molecules || '').toLowerCase() === 'yes' ? 'Combined' : 'Single',
        row.dosage_form || '',
        row.strength_value_1 ?? '',
        row.strength_unit_code_1 || '',
        isNL(row.market_auth_countries) ? 'Yes' : 'No',
        row.market_auth_countries || '',
        row.market_auth_status || row.ma_status || '',
        row.patent_status || '',
        row.dossier_availability_status || '',
        arrOrStr(row.therapeutical_indication) || '',
      ];
      win.document.write('<tr>' + cells.map(c => '<td>' + esc(c) + '</td>').join('') + '</tr>');
    });
    win.document.write('</tbody></table></body></html>');
    win.document.close();
    setTimeout(() => win.print(), 500);
    setShowDownloadMenu(false);
  };

  const filtersActive =
    !!filters.productId ||
    filters.supplier.length > 0 ||
    filters.product.length > 0 ||
    filters.form.length > 0 ||
    filters.type.length > 0 ||
    filters.dossier.length > 0 ||
    filters.ma.length > 0 ||
    !!filters.maCountries ||
    filters.patent.length > 0 ||
    filters.dossierAvail.length > 0 ||
    !!filters.dossierPrepDate ||
    filters.euAvail.length > 0 ||
    filters.hqCountry.length > 0 ||
    filters.productName.length > 0 ||
    filters.strength.length > 0 ||
    filters.unit.length > 0 ||
    filters.referenceProduct.length > 0 ||
    filters.atcCode.length > 0 ||
    filters.atcClassification.length > 0 ||
    filters.atcCategory.length > 0 ||
    filters.marketAuthStatus.length > 0 ||
    filters.therapeuticalIndication.length > 0 ||
    filters.patentExpiryDate.length > 0 ||
    filters.patentNotes.length > 0 ||
    filters.nlMaStatus.length > 0 ||
    (filters.productStatus && filters.productStatus.length > 0);
  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setActivePreset(null);
    navigate('/supplier-products', { replace: true });
  };

  const handleSort = (column) => {
    setSort(s => {
      if (s.column !== column) return { column, direction: 'asc' };
      if (s.direction === 'asc') return { column, direction: 'desc' };
      return { column: null, direction: 'asc' };
    });
  };

  const activePageIds = pageRows.filter(r => r.product_inactive !== 'Yes').map(r => r.id);
  const allPageChecked = activePageIds.length > 0 && activePageIds.every(id => selected.has(id));
  const toggleAll = () => {
    setSelected(prev => {
      const n = new Set(prev);
      if (allPageChecked) activePageIds.forEach(id => n.delete(id));
      else activePageIds.forEach(id => n.add(id));
      return n;
    });
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const onRowSaved = (updated) => {
    setRows(rs => rs.map(r => (r.id === (updated.id || updated.product_id) ? { ...r, ...updated } : r)));
    setEditRow(null);
    if (updated.product_inactive === 'Yes') {
      setSelected(prev => { const n = new Set(prev); n.delete(updated.id || updated.product_id); return n; });
    }
  };

  const onBulkSaved = () => {
    setBulkOpen(false);
    setSelected(new Set());
    load();
  };

  return (
    <div className={embedded ? 'sup-page sup-page-embedded' : 'sup-page'}>
      {/* Header — hidden when embedded in another page (e.g. Molecule Cockpit) */}
      {!embedded && (
        <div className="mb-3">
          <h2 className="sup-heading mb-1">All Supplier Products</h2>
          <div className="sup-breadcrumb">FocusOne &middot; Supplier Products</div>
        </div>
      )}

      {err && <div className="sup-error-box mb-3">{err}</div>}

      {!rows && !err && (
        <div className="sup-table-wrap p-4">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="sup-skeleton mb-2" style={{ height: 28, width: '100%' }} />
          ))}
        </div>
      )}

      {rows && (
        <>
          {/* Stats + filters bar */}
          <div className="sup-statsbar">
            <span className="sup-stat-gray">Total: {totalCount.toLocaleString('en-US')} products</span>
            <span className="sup-stat-sep" />
            <div className="sup-actions">
              {!embedded && (
                <button
                  type="button"
                  className="sup-btn primary"
                  onClick={() => setUploadOpen(true)}
                >
                  <i className="bi bi-plus-lg me-1"></i> New
                </button>
              )}
            </div>
          </div>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 8 }}>
            <div>
              Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length.toLocaleString('en-US')} products{distinctSupplierCount > 0 && filters.product.length > 0 && ` – From ${distinctSupplierCount} different suppliers in FocusOne`}{distinctSupplierCount > 0 && filters.product.length === 0 && ` – From ${distinctSupplierCount} different suppliers`}
            </div>
            {filters.product.length > 0 && externalLoading && (
              <div>Loading external register counts...</div>
            )}
            {filters.product.length > 0 && !externalLoading && externalCounts?.results?.map(r => (
              <div key={r.inn}>
                <div>{r.inn} found in {r.ema.product_count} products – From {r.ema.supplier_count} different suppliers in EMA Register</div>
                <div>{r.inn} found in {r.dutch.product_count} products – From {r.dutch.supplier_count} different suppliers in Dutch CBG-MEB</div>
                <div>{r.inn} found in {r.ansm.product_count} products – From {r.ansm.supplier_count} different suppliers in ANSM France</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, maxHeight: 140, overflowY: 'auto', overflowX: 'hidden' }}>
              <div className="sup-filters-row" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
            {visibleFilters.includes('productId') && (
              <div style={{ minWidth: 140 }}>
                <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Prod ID</div>
                <input
                  className="sup-input"
                  placeholder="Search by ID..."
                  value={filters.productId}
                  onChange={(e) => updateFilters(f => ({ ...f, productId: e.target.value.replace(/\D/g, '') }))}
                  style={{ padding: '5px 10px', fontSize: 12.5 }}
                />
              </div>
            )}
            {visibleFilters.includes('supplier') && (
              <MultiSelect
                label="Supplier"
                options={supplierOptions}
                values={filters.supplier}
                onChange={(v) => updateFilters(f => ({ ...f, supplier: v }))}
                placeholder="All Suppliers"
                minWidth={200}
              />
            )}
            {visibleFilters.includes('inn') && (
              <MultiSelect
                label="INN"
                options={productOptions}
                values={filters.product}
                onChange={(v) => updateFilters(f => ({ ...f, product: v }))}
                placeholder="All Products"
                minWidth={220}
              />
            )}
            {visibleFilters.includes('form') && (
              <MultiSelect
                label="Dosage Form"
                options={formOptions}
                values={filters.form}
                onChange={(v) => updateFilters(f => ({ ...f, form: v }))}
                placeholder="All Forms"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('type') && (
              <MultiSelect
                label="Molecule Type"
                options={['Single', 'Combined']}
                values={filters.type}
                onChange={(v) => updateFilters(f => ({ ...f, type: v }))}
                placeholder="All Types"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('maStatus') && (
              <MultiSelect
                label="MA Status"
                options={maOptions}
                values={filters.ma}
                onChange={(v) => updateFilters(f => ({ ...f, ma: v }))}
                placeholder="All MA Status"
                minWidth={180}
              />
            )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, paddingTop: 18 }}>
            {filtersActive && (
              <button type="button" className="sup-clear-link" onClick={clearFilters}>
                Clear All Filters
              </button>
            )}
              {/* Icon 1: Load Filter Preset */}
              <button
                onClick={() => setLoadFilterPresetOpen(true)}
                className="btn btn-sm"
                style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }}
                title="Load saved filter preset"
              >
                <i className="bi bi-funnel-fill"></i>
              </button>
              {/* Existing gear: Customize Filters */}
              <div ref={gearRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowFilterSettings(prev => !prev)}
                  className="btn btn-sm"
                  style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }}
                  title="Customize filters"
                >
                  <i className="bi bi-gear"></i>
                </button>
                {showFilterSettings && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                    background: 'var(--card)', border: '1px solid #dee2e6',
                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: 220, zIndex: 1000, padding: 12
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
                      Customize Filters
                    </div>
                    <div className="customize-filters-scroll" style={{ maxHeight: 280, overflowY: 'scroll', overflowX: 'hidden' }}>
                    {AVAILABLE_FILTERS.map(f => (
                      <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={(pendingFilters || visibleFilters).includes(f.key)}
                          onChange={() => {
                            const current = pendingFilters || visibleFilters;
                            const updated = current.includes(f.key)
                              ? current.filter(k => k !== f.key)
                              : [...current, f.key];
                            setPendingFilters(updated);
                          }}
                          style={{ accentColor: 'var(--teal)', width: 14, height: 14 }}
                        />
                        {f.label}
                      </label>
                    ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                      <button
                        onClick={saveFilterPreferences}
                        disabled={!pendingFilters}
                        style={{
                          background: pendingFilters ? 'var(--teal)' : 'var(--t3)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 16px',
                          fontSize: 12,
                          cursor: pendingFilters ? 'pointer' : 'not-allowed',
                          fontWeight: 500,
                          flex: 1,
                        }}
                      >
                        <i className="bi bi-check2 me-1"></i>
                        Save
                      </button>
                      <button
                        onClick={() => { setPendingFilters(null); setShowFilterSettings(false); }}
                        style={{
                          background: 'var(--card)',
                          color: 'var(--t3)',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 8 }}>
                      <button
                        onClick={() => { setShowFilterSettings(false); setSaveFilterPresetOpen(true); }}
                        style={{
                          background: 'none',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          color: 'var(--navy)',
                          fontWeight: 500,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                        }}
                      >
                        <i className="bi bi-floppy"></i>
                        Save as new preset
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* Icon 2: Load Column Preset */}
              <button
                onClick={() => setLoadColumnPresetOpen(true)}
                className="btn btn-sm"
                style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }}
                title="Load saved column visibility preset"
              >
                <i className="bi bi-layout-three-columns"></i>
              </button>
              {/* Icon 3: Column Manager Panel */}
              <div ref={columnManagerRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowColumnManager(prev => !prev)}
                  className="btn btn-sm"
                  style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }}
                  title="Manage column visibility and save as preset"
                >
                  <i className="bi bi-columns-gap"></i>
                </button>
                {showColumnManager && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                    background: 'var(--card)', border: '1px solid #dee2e6',
                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: 240, zIndex: 1000, padding: 12
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
                      Manage Columns
                    </div>
                    <input
                      className="sup-input"
                      placeholder="Search column..."
                      value={columnSearch}
                      onChange={(e) => setColumnSearch(e.target.value)}
                      style={{ padding: '5px 10px', fontSize: 12, marginBottom: 8 }}
                    />
                    <div className="customize-filters-scroll" style={{ maxHeight: 400, overflowY: 'scroll', overflowX: 'hidden' }}>
                      {(() => {
                        const isSearching = !!columnSearch;
                        const filtered = orderedColumns.filter(c => !columnSearch || c.label.toLowerCase().includes(columnSearch.toLowerCase()));
                        return filtered.map((c, idx) => (
                          <div
                            key={c.key}
                            draggable={!isSearching}
                            onDragStart={(e) => {
                              setPanelDragIdx(columnOrder.indexOf(c.key));
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragOver={(e) => {
                              if (isSearching) return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              e.currentTarget.style.borderTop = '2px solid var(--teal)';
                            }}
                            onDragLeave={(e) => { e.currentTarget.style.borderTop = ''; }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.borderTop = '';
                              if (panelDragIdx == null || isSearching) return;
                              const dragKey = columnOrder[panelDragIdx];
                              const dropKey = c.key;
                              if (dragKey !== dropKey) handleColumnDrop(dragKey, dropKey);
                              setPanelDragIdx(null);
                            }}
                            onDragEnd={() => setPanelDragIdx(null)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', fontSize: 13, cursor: isSearching ? 'default' : 'grab', userSelect: 'none' }}
                          >
                            {!isSearching && (
                              <span style={{ color: '#9ca3af', fontSize: 11, lineHeight: 1, flexShrink: 0, width: 14, textAlign: 'center' }}>⋮⋮</span>
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1, margin: 0 }} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.has(c.key)}
                                onChange={() => {
                                  setVisibleColumns(prev => {
                                    const next = new Set(prev);
                                    if (next.has(c.key)) next.delete(c.key);
                                    else next.add(c.key);
                                    return next;
                                  });
                                }}
                                style={{ accentColor: 'var(--teal)', width: 14, height: 14 }}
                              />
                              {c.label}
                            </label>
                          </div>
                        ));
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setVisibleColumns(new Set(ALL_COL_KEYS))}
                        disabled={visibleColumns.size === ALL_TABLE_COLUMNS.length}
                        style={{
                          background: visibleColumns.size < ALL_TABLE_COLUMNS.length ? 'var(--card)' : '#f8f9fa',
                          color: visibleColumns.size < ALL_TABLE_COLUMNS.length ? 'var(--navy)' : 'var(--t3)',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: visibleColumns.size < ALL_TABLE_COLUMNS.length ? 'pointer' : 'not-allowed',
                          fontWeight: 500,
                          flex: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Reset to All
                      </button>
                      <button
                        onClick={() => {
                          if (!window.confirm('Reset column order to default? This will overwrite your current arrangement.')) return;
                          setColumnOrder(DEFAULT_COL_ORDER);
                          saveColumnOrder(DEFAULT_COL_ORDER);
                          setToast({ type: 'ok', msg: 'Column order reset.' });
                        }}
                        disabled={JSON.stringify(columnOrder) === JSON.stringify(DEFAULT_COL_ORDER)}
                        style={{
                          background: JSON.stringify(columnOrder) !== JSON.stringify(DEFAULT_COL_ORDER) ? 'var(--card)' : '#f8f9fa',
                          color: JSON.stringify(columnOrder) !== JSON.stringify(DEFAULT_COL_ORDER) ? 'var(--navy)' : 'var(--t3)',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: JSON.stringify(columnOrder) !== JSON.stringify(DEFAULT_COL_ORDER) ? 'pointer' : 'not-allowed',
                          fontWeight: 500,
                          flex: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ↻ Reset order
                      </button>
                      <button
                        onClick={() => { setShowColumnManager(false); setSaveColumnPresetOpen(true); }}
                        style={{
                          background: 'none',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          color: 'var(--navy)',
                          fontWeight: 500,
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="bi bi-floppy"></i>
                        Save as preset
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* Active preset badge */}
              {activePreset && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: '#eef4fb', border: '1px solid #bfdbfe', borderRadius: 16,
                  padding: '3px 10px', fontSize: 11, color: 'var(--navy)', whiteSpace: 'nowrap'
                }}>
                  Active: {activePreset}
                  <button type="button" onClick={() => setActivePreset(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1, color: '#6b7280', padding: 0 }}>&times;</button>
                </span>
              )}
              <div ref={downloadRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowDownloadMenu(prev => !prev)}
                  className="btn btn-sm"
                  style={{
                    background: 'var(--teal)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  title="Download"
                >
                  <i className="bi bi-download"></i>
                </button>
                {showDownloadMenu && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                    background: 'var(--card)', border: '1px solid #dee2e6',
                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: 140, zIndex: 1000, overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => { handleDownloadCSV(); setShowDownloadMenu(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'var(--card)', cursor: 'pointer', fontSize: 13, color: 'var(--t1)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EFF8FF'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}
                    >
                      <i className="bi bi-file-earmark-spreadsheet" style={{ color: 'var(--teal)' }}></i>
                      Download CSV
                    </button>
                    <div style={{ height: 1, background: '#f0f0f0' }}></div>
                    <button
                      onClick={handleDownloadPDF}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'var(--card)', cursor: 'pointer', fontSize: 13, color: 'var(--t1)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EFF8FF'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}
                    >
                      <i className="bi bi-file-earmark-pdf" style={{ color: '#D63B0A' }}></i>
                      Download PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ maxHeight: 140, overflowY: 'auto', overflowX: 'hidden', marginBottom: 8 }}>
          <div className="sup-filters-row" style={{ paddingTop: 0, marginBottom: 0, flexWrap: 'wrap' }}>
            {visibleFilters.includes('marketAuthCountries') && (
              <div style={{ minWidth: 180 }}>
                <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>MA Availability</div>
                <input
                  className="sup-input"
                  placeholder="Type to filter…"
                  value={filters.maCountries}
                  onChange={(e) => updateFilters(f => ({ ...f, maCountries: e.target.value }))}
                  style={{ padding: '5px 10px', fontSize: 12.5 }}
                />
              </div>
            )}
            {visibleFilters.includes('patentStatus') && (
              <MultiSelect
                label="Patent Status"
                options={patentOptions}
                values={filters.patent}
                onChange={(v) => updateFilters(f => ({ ...f, patent: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('dossierAvailability') && (
              <MultiSelect
                label="Dossier Status"
                options={dossierAvailOpt}
                values={filters.dossierAvail}
                onChange={(v) => updateFilters(f => ({ ...f, dossierAvail: v }))}
                placeholder="All"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('dossierPrepDate') && (
              <div style={{ minWidth: 160 }}>
                <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Expected</div>
                <input
                  className="sup-input"
                  placeholder="Type to filter…"
                  value={filters.dossierPrepDate}
                  onChange={(e) => updateFilters(f => ({ ...f, dossierPrepDate: e.target.value }))}
                  style={{ padding: '5px 10px', fontSize: 12.5 }}
                />
              </div>
            )}
            {visibleFilters.includes('euMarketAvailability') && (
              <MultiSelect
                label="EU Market Availability"
                options={['YES', 'No', 'N/A']}
                values={filters.euAvail}
                onChange={(v) => updateFilters(f => ({ ...f, euAvail: v }))}
                placeholder="All"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('hqCountry') && (
              <MultiSelect
                label="Supplier HQ"
                options={hqCountryEffective}
                values={filters.hqCountry}
                onChange={(v) => updateFilters(f => ({ ...f, hqCountry: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('productName') && (
              <MultiSelect
                label="Product Name"
                options={productNameOptions}
                values={filters.productName}
                onChange={(v) => updateFilters(f => ({ ...f, productName: v }))}
                placeholder="All"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('strength') && (
              <MultiSelect
                label="Strength"
                options={strengthOptions}
                values={filters.strength}
                onChange={(v) => updateFilters(f => ({ ...f, strength: v }))}
                placeholder="All"
                minWidth={140}
              />
            )}
            {visibleFilters.includes('unit') && (
              <MultiSelect
                label="Strength Unit"
                options={unitOptions}
                values={filters.unit}
                onChange={(v) => updateFilters(f => ({ ...f, unit: v }))}
                placeholder="All"
                minWidth={140}
              />
            )}
            {visibleFilters.includes('referenceProduct') && (
              <MultiSelect
                label="Reference Product"
                options={referenceProductOptions}
                values={filters.referenceProduct}
                onChange={(v) => updateFilters(f => ({ ...f, referenceProduct: v }))}
                placeholder="All"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('atcCode') && (
              <MultiSelect
                label="ATC Code"
                options={atcCodeOptions}
                values={filters.atcCode}
                onChange={(v) => updateFilters(f => ({ ...f, atcCode: v }))}
                placeholder="All"
                minWidth={140}
              />
            )}
            {visibleFilters.includes('atcClassification') && (
              <MultiSelect
                label="ATC Classification"
                options={atcClassificationOptions}
                values={filters.atcClassification}
                onChange={(v) => updateFilters(f => ({ ...f, atcClassification: v }))}
                placeholder="All"
                minWidth={180}
              />
            )}
            {visibleFilters.includes('atcCategory') && (
              <MultiSelect
                label="ATC Category"
                options={atcCategoryOptions}
                values={filters.atcCategory}
                onChange={(v) => updateFilters(f => ({ ...f, atcCategory: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('marketAuthStatus') && (
              <MultiSelect
                label="Supplier MA Status"
                options={marketAuthStatusOptions}
                values={filters.marketAuthStatus}
                onChange={(v) => updateFilters(f => ({ ...f, marketAuthStatus: v }))}
                placeholder="All"
                minWidth={200}
              />
            )}
            {visibleFilters.includes('therapeuticalIndication') && (
              <MultiSelect
                label="Therapeutic Indication"
                options={therIndOptions}
                values={filters.therapeuticalIndication}
                onChange={(v) => updateFilters(f => ({ ...f, therapeuticalIndication: v }))}
                placeholder="All"
                minWidth={200}
              />
            )}
            {visibleFilters.includes('patentExpiryDate') && (
              <MultiSelect
                label="Patent Expiry Date"
                options={patentExpiryOptions}
                values={filters.patentExpiryDate}
                onChange={(v) => updateFilters(f => ({ ...f, patentExpiryDate: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('patentNotes') && (
              <MultiSelect
                label="Patent Notes"
                options={patentNotesOptions}
                values={filters.patentNotes}
                onChange={(v) => updateFilters(f => ({ ...f, patentNotes: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
            {visibleFilters.includes('nlMaStatus') && (
              <MultiSelect
                label="NL MA Status"
                options={nlMaStatusOptions}
                values={filters.nlMaStatus}
                onChange={(v) => updateFilters(f => ({ ...f, nlMaStatus: v }))}
                placeholder="All"
                minWidth={160}
              />
            )}
          </div>
          </div>

          {selected.size > 0 && (
            <div className="sup-selbar">
              <span>{selected.size} items selected</span>
              <button type="button" className="sup-btn primary" onClick={() => setBulkOpen(true)}>
                Edit Selected
              </button>
            </div>
          )}

          <div className="sup-table-wrap">
            <style>{`
@keyframes spSwitchBlink { 0%,100%{opacity:1} 50%{opacity:.35} }
.sp-switch-blink { animation: spSwitchBlink 1.5s ease-in-out infinite; }
.table-scroll-container::-webkit-scrollbar { height: 8px; width: 8px; }
.table-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; }
.table-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.table-top-scroll::-webkit-scrollbar { height: 8px; }
.table-top-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
.table-top-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-top-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll::-webkit-scrollbar { width: 8px; }
.customize-filters-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
.sup-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
${columnHideCss}
            `}</style>
            <div
              ref={topScrollRef}
              className="table-top-scroll"
              onScroll={syncFromTop}
              style={{ overflowX: 'auto', overflowY: 'hidden', height: 12, marginBottom: 4 }}
            >
              <div style={{ height: 1 }} />
            </div>
            <div
              ref={tableScrollRef}
              className="table-scroll-container"
              onScroll={syncFromTable}
              style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', maxHeight: '72vh' }}
            >
              <table className="sup-table" style={{ minWidth: 5200, tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" checked={allPageChecked} onChange={toggleAll} aria-label="Select all on page" />
                    </th>
                    <th style={{ width: 60 }}>Open</th>
                    <th style={{ width: 60 }}>Remove</th>
                    <th style={{ width: 60 }}>Active</th>
                    <th style={{ width: 32 }} title="Switch Status"></th>
                    {orderedColumns.map(c => (
                      <SortHead key={c.key} label={c.label} column={c.key} sort={sort} onSort={handleSort}
                        colWidth={columnWidths[c.key]} onResizeStart={startResize} onAutoFit={autoFitColumn}
                        data-col={c.key}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(r => {
                    const isSel = selected.has(r.id);
                    const combined = String(r.combines_molecules || '').toLowerCase() === 'yes';
                    const nl = isNL(r.market_auth_countries);
                    const supplierMaCls = maBadgeClass(r.supplier_ma_status);
                    const cellRenderers = {
                      product_id: () => r.product_id || '—',
                      source_type: () => r.source_type || '—',
                      document_name_reference: () => r.document_name_reference ? (
                        <a href={`${import.meta.env.VITE_API_URL}/api/documents/${encodeURIComponent(r.document_name_reference)}`} target="_blank" rel="noopener noreferrer" title={r.document_name_reference} className="sup-truncate" style={{ color: '#1d4ed8', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{truncate(r.document_name_reference, 30)}</a>
                      ) : '—',
                      supplier_id: () => r.supplier_id || '—',
                      supplier_name: () => <span className="sup-inn">{r.supplier_name || '—'}</span>,
                      supplier_headquarter_country: () => r.supplier_headquarter_country || '—',
                      manufacturing_sites: () => <span className="sup-truncate" title={r.manufacturing_sites || ''}>{truncate(r.manufacturing_sites, 30)}</span>,
                      supplier_product_name: () => <span className="sup-prod-name">{r.supplier_product_name || '—'}</span>,
                      product_status: () => r.product_inactive === 'Yes' ? (
                        <span title="Reactivate product to change status" style={{ display: 'inline-block', width: 110, padding: '3px 6px', fontSize: 12, color: 'var(--t3)', fontWeight: 500, cursor: 'not-allowed' }}>{normalizeStatus(r.product_status)}</span>
                      ) : (
                        <select value={normalizeStatus(r.product_status)} disabled={!r.id} onChange={(e) => { const newVal = e.target.value; if (newVal === normalizeStatus(r.product_status)) return; setStatusConfirm({ row: r, newStatus: newVal, oldStatus: r.product_status }); }} style={{ width: 110, padding: '3px 6px', fontSize: 12, border: '1px solid #dee2e6', borderRadius: 6, background: 'white', color: statusColor(normalizeStatus(r.product_status)), fontWeight: 500, cursor: 'pointer' }}>{STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select>
                      ),
                      inn: () => <span className="sup-inn">{r.inn || '—'}</span>,
                      combines_molecules: () => <span className={`sup-badge ${combined ? 'purple-soft' : 'teal-soft'}`}>{combined ? 'Combined' : 'Single'}</span>,
                      strength_value_1: () => r.strength_value_1 ?? '—',
                      strength_unit_code_1: () => r.strength_unit_code_1 || '—',
                      dosage_form: () => r.dosage_form || '—',
                      full_strength: () => <span className="sup-truncate" title={r.full_strength || ''}>{truncate(r.full_strength, 30)}</span>,
                      pack_size: () => r.pack_size || '—',
                      reference_product_name: () => <span className="sup-truncate" title={r.reference_product_name || ''}>{truncate(r.reference_product_name, 30)}</span>,
                      therapeutical_indication: () => <span className="sup-truncate" title={arrOrStr(r.therapeutical_indication)}>{truncate(arrOrStr(r.therapeutical_indication), 30)}</span>,
                      atc_code: () => r.atc_code || '—',
                      atc_classification_name: () => <span className="sup-truncate" title={r.atc_classification_name || ''}>{truncate(r.atc_classification_name, 30)}</span>,
                      atc_category: () => r.atc_category || '—',
                      dossier_availability_status: () => r.dossier_availability_status || '—',
                      dossier_preparation_planned_date: () => r.dossier_preparation_planned_date || '—',
                      market_auth_countries: () => <span className="sup-truncate" title={r.market_auth_countries || ''}>{truncate(r.market_auth_countries, 30)}</span>,
                      market_auth_status: () => r.market_auth_status || '—',
                      supplier_ma_status: () => r.supplier_ma_status ? (supplierMaCls ? <span className={`sup-badge ${supplierMaCls}`}>{r.supplier_ma_status}</span> : <span>{r.supplier_ma_status}</span>) : '—',
                      ma_additional_info: () => <span className="sup-truncate" title={r.ma_additional_info || ''}>{truncate(r.ma_additional_info, 30)}</span>,
                      additional_comments: () => <span className="sup-truncate" title={arrOrStr(r.additional_comments)}>{truncate(arrOrStr(r.additional_comments), 30)}</span>,
                      nl_market_available: () => nl ? <span className="sup-check" aria-label="Yes">✓</span> : <span className="sup-cross" aria-label="No">✗</span>,
                      nl_general_ma_status: () => r.nl_general_ma_status || '—',
                      ma_registration_number: () => r.ma_registration_number || '—',
                      ma_approved_by: () => r.ma_approved_by || '—',
                      patent_status: () => r.patent_status || '—',
                      patent_expiry_date: () => r.patent_expiry_date || '—',
                      patent_notes: () => <span className="sup-truncate" title={r.patent_notes || ''}>{truncate(r.patent_notes, 30)}</span>,
                      created_at: () => fmtDate(r.created_at),
                      uploaded_by: () => displayUploadedBy(r.created_by),
                      created_by: () => r.created_by || '—',
                      updated_at: () => fmtDate(r.updated_at),
                      updated_by: () => r.updated_by || '—',
                      molecule_name: () => <span className="sup-truncate" title={r.molecule_name || ''}>{truncate(r.molecule_name, 30)}</span>,
                      molecule_comments: () => <span className="sup-truncate" title={r.molecule_comments || ''}>{truncate(r.molecule_comments, 30)}</span>,
                      dosage_form_note: () => <span className="sup-truncate" title={r.dosage_form_note || ''}>{truncate(r.dosage_form_note, 30)}</span>,
                      therapeutic_indications: () => <span className="sup-truncate" title={arrOrStr(r.therapeutic_indications)}>{truncate(arrOrStr(r.therapeutic_indications), 30)}</span>,
                      supplier_availability: () => r.supplier_availability || '—',
                      reference_date: () => r.reference_date || '—',
                      file_description: () => r.file_description || '—',
                    };
                    return (
                      <tr key={r.id} className={isSel ? 'selected' : ''}>
                        <td>
                          <input type="checkbox" checked={isSel} onChange={() => toggleOne(r.id)} disabled={r.product_inactive === 'Yes'} title={r.product_inactive === 'Yes' ? 'Inactive products cannot be selected for bulk actions' : undefined} style={r.product_inactive === 'Yes' ? { cursor: 'not-allowed', opacity: 0.4 } : undefined} />
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white' }} title="Edit" onClick={() => setEditRow(r)}>
                            <i className="bi bi-box-arrow-up-right" style={{ color: 'var(--teal)', fontSize: 14 }}></i>
                          </button>
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white', cursor: r.product_inactive === 'Yes' ? 'not-allowed' : 'pointer', opacity: r.product_inactive === 'Yes' ? 0.4 : 1 }} title="Remove product" disabled={r.product_inactive === 'Yes'} onClick={() => setRemoveConfirm(r)} onMouseEnter={e => { if (r.product_inactive !== 'Yes') e.currentTarget.querySelector('i').style.color = '#dc2626'; }} onMouseLeave={e => { e.currentTarget.querySelector('i').style.color = '#9ca3af'; }}>
                            <i className="bi bi-trash" style={{ color: '#9ca3af', fontSize: 14 }}></i>
                          </button>
                        </td>
                        <td>
                          {r.product_inactive === 'Yes'
                            ? <span style={{ color: '#dc2626', fontSize: 16 }} aria-label="Inactive">&#10060;</span>
                            : <span style={{ color: '#16a34a', fontSize: 16 }} aria-label="Active">&#9989;</span>}
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle', overflow: 'visible', maxWidth: 'none', textOverflow: 'clip' }}>
                          {(() => {
                            const sw = switchStatusMap[(r.inn || '').toUpperCase().trim()] || 'green';
                            const swColor = sw === 'red' ? '#dc2626' : sw === 'orange' ? '#f59e0b' : '#16a34a';
                            const clickable = sw === 'red' || sw === 'orange';
                            return (
                              <span
                                className="sp-switch-blink"
                                title={sw === 'red' ? 'Red List' : sw === 'orange' ? 'Orange List' : 'Green'}
                                onClick={clickable ? () => navigate(`/market-intelligence/${sw}-list`) : undefined}
                                style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: swColor, cursor: clickable ? 'pointer' : 'default' }}
                              />
                            );
                          })()}
                        </td>
                        {orderedColumns.map(c => (
                          <td key={c.key}>{cellRenderers[c.key] ? cellRenderers[c.key]() : (r[c.key] || '—')}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
              <button
                type="button"
                onClick={() => { if (pageSize < sorted.length) { setPageSize(sorted.length); } else { setPageSize(DEFAULT_PAGE_SIZE); } setPage(1); }}
                style={{ fontSize: 12, color: 'var(--teal)', border: '1px solid var(--teal)', background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {pageSize < sorted.length ? 'Show All' : `Show ${DEFAULT_PAGE_SIZE}`}
              </button>
              <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
                  {(() => {
                    const win = 5;
                    let s = Math.max(1, safePage - Math.floor(win / 2));
                    let e = Math.min(pageCount, s + win - 1);
                    s = Math.max(1, e - win + 1);
                    const nums = [];
                    for (let i = s; i <= e; i++) nums.push(i);
                    return nums.map(n => (
                      <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
                    ));
                  })()}
                  <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
              </nav>
              <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'product' : 'products'}
              </span>
            </div>
          </div>
        </>
      )}

      {editRow && (
        <SingleEditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={onRowSaved}
          onToast={setToast}
          dosageForms={dosageForms}
          strengthUnits={strengthUnits}
          editorName={editorName}
          acquireToken={acquireToken}
        />
      )}

      {bulkOpen && (
        <BulkEditModal
          count={selected.size}
          ids={Array.from(selected)}
          onClose={() => setBulkOpen(false)}
          onSaved={onBulkSaved}
          onToast={setToast}
          editorName={editorName}
        />
      )}

      {csvOpen && (
        <CsvUploadModal
          onClose={() => setCsvOpen(false)}
          onDone={() => { setCsvOpen(false); load(); }}
          onToast={setToast}
        />
      )}

      {uploadOpen && (
        <UploadProductsModal
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); load(); }}
          onToast={setToast}
        />
      )}

      {uploadResultModal && (
        <div className="sup-modal-backdrop" onClick={() => setUploadResultModal(null)}>
          <div className="sup-modal upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0"><span className="sup-title-main">Upload Result</span></h5>
              <button type="button" className="sup-modal-close" onClick={() => setUploadResultModal(null)}>x</button>
            </div>
            <div className="sup-modal-body">
              {uploadResultModal.status === 'failed' ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 14, color: '#e74c3c', fontWeight: 600, marginBottom: 8 }}>Upload Failed</div>
                  <div style={{ fontSize: 13, color: 'var(--t2)' }}>{uploadResultModal.message || 'Processing failed'}</div>
                  <button type="button" className="sup-btn primary" style={{ marginTop: 16 }} onClick={() => setUploadResultModal(null)}>Close</button>
                </div>
              ) : (
                <UploadSummary
                  data={uploadResultModal}
                  fileDescription={uploadResultModal.fileDescription || uploadResultModal.file_description}
                  referenceDate={uploadResultModal.referenceDate || uploadResultModal.reference_date}
                  supplierName={uploadResultModal.supplierName || uploadResultModal.supplier_name}
                  filename={uploadResultModal.filename || uploadResultModal.original_filename}
                  onClose={() => setUploadResultModal(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {removeConfirm && (
        <RemoveConfirmModal
          row={removeConfirm}
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={async () => {
            const r = removeConfirm;
            setRemoveConfirm(null);
            try {
              await deleteProduct(r.id, editorName);
              setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_inactive: 'Yes', product_deletion_date: new Date().toISOString(), deleted_by: editorName } : x));
              setSelected(prev => { const n = new Set(prev); n.delete(r.id); return n; });
              setToast({ type: 'ok', msg: 'Product marked as inactive.' });
            } catch (err) {
              setToast({ type: 'error', msg: err?.response?.data?.error || 'Failed to remove product' });
            }
          }}
        />
      )}

      {statusConfirm && (
        <StatusConfirmModal
          productName={statusConfirm.row.supplier_product_name}
          newStatus={statusConfirm.newStatus}
          onCancel={() => setStatusConfirm(null)}
          onConfirm={async () => {
            const { row: r, newStatus, oldStatus } = statusConfirm;
            setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_status: newStatus } : x));
            setStatusConfirm(null);
            try {
              await patchProductStatus(r.id, newStatus, editorName);
            } catch {
              setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_status: oldStatus } : x));
              setToast({ type: 'error', msg: 'Failed to update status' });
            }
          }}
        />
      )}

      {/* Preset modals */}
      {loadFilterPresetOpen && (
        <LoadPresetModal
          title="Load Filter Preset"
          presetType="filter"
          scope="supplier-products"
          msalInstance={msalInstance}
          onToast={setToast}
          onClose={() => setLoadFilterPresetOpen(false)}
          onApply={(cfg, name) => {
            if (cfg.visible_filters && Array.isArray(cfg.visible_filters)) {
              setVisibleFilters(cfg.visible_filters);
            }
            if (cfg.filter_values && typeof cfg.filter_values === 'object') {
              setFilters(prev => {
                const next = { ...prev };
                for (const [k, v] of Object.entries(cfg.filter_values)) {
                  if (k in next) next[k] = v;
                }
                return next;
              });
            }
            setActivePreset(name);
          }}
        />
      )}
      {loadColumnPresetOpen && (
        <LoadPresetModal
          title="Load Column Preset"
          presetType="column"
          scope="supplier-products"
          msalInstance={msalInstance}
          onToast={setToast}
          onClose={() => setLoadColumnPresetOpen(false)}
          onApply={(cfg, name) => {
            if (cfg.visible_columns && Array.isArray(cfg.visible_columns)) {
              setVisibleColumns(new Set(cfg.visible_columns));
            }
            if (cfg.column_order && Array.isArray(cfg.column_order)) {
              const savedSet = new Set(cfg.column_order);
              const valid = cfg.column_order.filter(k => ALL_COL_KEYS.includes(k));
              const missing = ALL_COL_KEYS.filter(k => !savedSet.has(k));
              setColumnOrder([...valid, ...missing]);
            }
            setActivePreset(name);
          }}
        />
      )}
      {saveColumnPresetOpen && (
        <SavePresetModal
          title="Save Column Preset"
          presetType="column"
          scope="supplier-products"
          msalInstance={msalInstance}
          onToast={setToast}
          onClose={() => setSaveColumnPresetOpen(false)}
          configJsonFn={() => ({
            visible_columns: Array.from(visibleColumns),
            column_order: columnOrder,
          })}
        />
      )}
      {saveFilterPresetOpen && (
        <SavePresetModal
          title="Save Filter Preset"
          presetType="filter"
          scope="supplier-products"
          msalInstance={msalInstance}
          onToast={setToast}
          onClose={() => setSaveFilterPresetOpen(false)}
          configJsonFn={() => ({
            visible_filters: visibleFilters,
            filter_values: filters,
          })}
        />
      )}

      {toast && (
        <div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
